import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MaterialCommunityIcons,
} from "@expo/vector-icons";
import {
  ActivityIndicator,
  Alert,
  Animated,
  Easing,
  FlatList,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import QRCode from "react-native-qrcode-svg";
import AllergenBadges from "../components/AllergenBadges";
import { getProductImageUrl } from "../utils/urls";
import i18n from "../utils/localization";

const KIOSK_PRODUCTS_QUERY = gql`
  query KioskProducts($token: String!) {
    currency(token: $token)
    categories(token: $token) {
      id
      name
      order
    }
    products(token: $token) {
      id
      category
      title
      subtitle
      imageUrls
      published
      available
      variants
      volumes
      allergens
      prices
      order
      options {
        id
        type
        title
        required
        options {
          id
          title
          prices
        }
      }
    }
  }
`;

const KIOSK_DEVICE_LOGIN_QUERY = gql`
  query KioskDeviceLogin($code: String!) {
    kioskLogin(code: $code) {
      kioskName
      operatorId
      logoUrl
      defaultPosId
      adUrl
    }
  }
`;

const CREATE_ONLINE_ORDER = gql`
  mutation CreateOnlineOrder($input: OnlineOrderInput!) {
    createOnlineOrder(input: $input) {
      id
      orderNumber
      publicToken
      status
      paymentMethod
      paymentStatus
      totals {
        orderGross
      }
    }
  }
`;

const THEMES = {
  white: {
    name: "white",
    bg: "#fff4e6",
    panel: "#fffdf8",
    panelSoft: "#fff0dc",
    text: "#402516",
    textMuted: "#8b6f5d",
    line: "#f2d4ba",
    shadow: "#b79379",
    accent: "#d97706",
    accentSecondary: "#ea580c",
    accentSoft: "#ffe3c4",
    danger: "#c2410c",
    warning: "#b45309",
  },
  sunset: {
    name: "sunset",
    bg: "#fff1ea",
    panel: "#fffaf6",
    panelSoft: "#ffe5d6",
    text: "#3f1f19",
    textMuted: "#8f5f54",
    line: "#f6c8b3",
    shadow: "#a85d49",
    accent: "#ff6b35",
    accentSecondary: "#ff9f1c",
    accentSoft: "#ffd9c2",
    danger: "#c53030",
    warning: "#b7791f",
  },
  festival: {
    name: "festival",
    bg: "#fff4de",
    panel: "#fff9ef",
    panelSoft: "#ffe7b8",
    text: "#3a1f14",
    textMuted: "#8f5c43",
    line: "#f7c38f",
    shadow: "#9e4d2f",
    accent: "#ff3d81",
    accentSecondary: "#ff8a00",
    accentSoft: "#ffd2e4",
    danger: "#d7263d",
    warning: "#c65d00",
  },
  italoamerican: {
    name: "italoamerican",
    bg: "#ffffff",
    panel: "#ffffff",
    panelSoft: "#e8efff",
    text: "#1f2937",
    textMuted: "#4b5563",
    line: "#9db7ff",
    shadow: "#1f3a8a",
    accent: "#128a45",
    accentSecondary: "#d62828",
    accentSoft: "#daf5e4",
    danger: "#b91c1c",
    warning: "#c65d00",
  },
  lagoon: {
    name: "lagoon",
    bg: "#e8fbff",
    panel: "#f7feff",
    panelSoft: "#d8f4ff",
    text: "#0f2e3a",
    textMuted: "#4c7382",
    line: "#a8e0ef",
    shadow: "#3c7a8f",
    accent: "#0ea5e9",
    accentSecondary: "#06b6d4",
    accentSoft: "#cceefe",
    danger: "#dc2626",
    warning: "#d97706",
  },
  citrus: {
    name: "citrus",
    bg: "#fffbe8",
    panel: "#fffef6",
    panelSoft: "#fff6cb",
    text: "#3a3412",
    textMuted: "#7f7542",
    line: "#f2e5a3",
    shadow: "#8d7e2e",
    accent: "#84cc16",
    accentSecondary: "#f59e0b",
    accentSoft: "#eaf8c8",
    danger: "#b91c1c",
    warning: "#b45309",
  },
  rose: {
    name: "rose",
    bg: "#fff0f6",
    panel: "#fff9fc",
    panelSoft: "#ffddeb",
    text: "#3b1f2d",
    textMuted: "#815a6f",
    line: "#f5bdd5",
    shadow: "#9a4d72",
    accent: "#ec4899",
    accentSecondary: "#f43f5e",
    accentSoft: "#ffd1e7",
    danger: "#be123c",
    warning: "#c2410c",
  },
  tropic: {
    name: "tropic",
    bg: "#ecfff6",
    panel: "#f8fffb",
    panelSoft: "#d9fbe9",
    text: "#123428",
    textMuted: "#4f7d6f",
    line: "#a5e8c8",
    shadow: "#3a8b6f",
    accent: "#10b981",
    accentSecondary: "#f97316",
    accentSoft: "#c9f7e2",
    danger: "#dc2626",
    warning: "#b45309",
  },
  dark: {
    name: "dark",
    bg: "#07111f",
    panel: "#0e2037",
    panelSoft: "#102643",
    text: "#eef7ff",
    textMuted: "#8ea4bc",
    line: "#294764",
    shadow: "#000000",
    accent: "#21c8ff",
    accentSecondary: "#2dd4bf",
    accentSoft: "#0f2e4f",
    danger: "#f87171",
    warning: "#fbbf24",
  },
};
const DEFAULT_THEME = "italoamerican";
const THEME_LABELS = {
  white: {
    en: "Mexicali",
    bg: "Mexicali",
    it: "Mexicali",
  },
  sunset: {
    en: "Tex-Mex",
    bg: "Tex-Mex",
    it: "Tex-Mex",
  },
  festival: {
    en: "California Dreamin'",
    bg: "California Dreamin'",
    it: "California Dreamin'",
  },
  italoamerican: {
    en: "Italo-Americano",
    bg: "Итало-американска",
    it: "Italo-Americano",
  },
  lagoon: {
    en: "Pacific Rim",
    bg: "Pacific Rim",
    it: "Pacific Rim",
  },
  citrus: {
    en: "Port Royal",
    bg: "Port Royal",
    it: "Port Royal",
  },
  rose: {
    en: "Rose Candy",
    bg: "Розов бонбон",
    it: "Rosa Candy",
  },
  tropic: {
    en: "Tropic Mint",
    bg: "Тропик мента",
    it: "Menta Tropicale",
  },
  dark: {
    en: "Midnight",
    bg: "Среднощ",
    it: "Mezzanotte",
  },
};
const PRODUCT_LIST_INITIAL_RENDER = 6;
const PRODUCT_LIST_BATCH_SIZE = 6;
const PRODUCT_LIST_WINDOW_SIZE = 7;
const WELCOME_AD_MIN_HEIGHT = 300;
const WELCOME_AD_MAX_HEIGHT = 560;
const THANK_YOU_AUTO_RESET_MS = 20000;
const THANK_YOU_CONFETTI_COLORS = ["#ff3d81", "#10b981", "#0ea5e9", "#f59e0b", "#d62828", "#ffffff"];
const NativeWebView = Platform.OS === "web" ? null : require("react-native-webview").WebView;
const KIOSK_DEVICE_CONFIG_STORAGE_KEY = "kioskDeviceConfig";
const KIOSK_DEVICE_CODE_STORAGE_KEY = "kioskDeviceCode";
const SECRET_SETTINGS_SEQUENCE = ["top-left", "top-right", "bottom-right", "bottom-left", "top-center-hold"];
const SECRET_SETTINGS_TIMEOUT_MS = 4000;

const CATEGORY_ALL = "all";
const RECEIPT_METHOD_EMAIL = "email";
const RECEIPT_METHOD_PHONE = "phone";
const RECEIPT_METHOD_SCREEN = "screen";
const FULFILLMENT_PICKUP = "pickup";
const FULFILLMENT_TABLE = "table";
const FULFILLMENT_DELIVERY = "delivery";

// No service fee today. Kept as a seam so a real fee can be reintroduced later without
// re-plumbing the totals; while it is 0 the fee row is hidden and total === subtotal.
const KIOSK_SERVICE_FEE_RATE = 0;
const PAYMENT_METHOD_QR = "qr";
const PAYMENT_METHOD_TAP = "tap";
const LANGUAGE_LABELS = {
  bg: "Български",
  en: "English",
  it: "Italiano",
};
const LANGUAGE_FLAGS = {
  bg: "🇧🇬",
  en: "🇬🇧",
  it: "🇮🇹",
};

const WELCOME_COPY = {
  en: {
    venue: "Venue Kiosk",
    welcome: "Welcome",
    subtitle: "Tap to start your order and enjoy quick self-checkout.",
    chooseLanguage: "Choose language",
    chooseTheme: "Choose color theme",
    startOrder: "Start Order",
    devicePreparing: "Preparing kiosk...",
    deviceLoginKicker: "Device Login",
    deviceLoginTitle: "Configure This Kiosk",
    deviceLoginSubtitle: "Enter the kiosk code to fetch this device configuration.",
    kioskCodeLabel: "Kiosk Code",
    kioskCodePlaceholder: "Example: KIOSK-01",
    autoCodePrefix: "Auto code from URL",
    loginKioskButton: "Login Kiosk",
    loginKioskButtonLoading: "Configuring...",
    deviceErrorEnterCode: "Enter your kiosk login code.",
    deviceErrorInvalidCode: "Invalid kiosk code. Check and try again.",
    deviceErrorUnavailable: "Could not configure kiosk right now.",
    orderHere: "ORDER HERE",
    noImage: "No Image",
    singleSize: "Single size",
    add: "Add",
    allCategory: "All",
    loadingProducts: "Loading products...",
    loadProductsFailed: "Unable to load products",
    retry: "Retry",
    orderInSeconds: "Order In Seconds",
    browseSubtitle: "Pick variants on each card, then choose extras after tapping Add.",
    startOver: "Start Over",
    noProductsInCategory: "No products in this category.",
    itemInCart: "item in cart",
    itemsInCart: "items in cart",
    reviewOrder: "Review Order",
    reviewSubtitle: "Review your items before checkout.",
    itemNoteLabel: "Item Note",
    itemNotePlaceholder: "No onions, extra sauce, allergies...",
    orderNoteLabel: "Order Note",
    orderNotePlaceholder: "Anything else for the full order?",
    emptyCart: "Your cart is empty. Add products to continue.",
    subtotal: "Subtotal",
    service: "Service",
    total: "Total",
    checkoutReceiptTitle: "Receipt Preference",
    checkoutReceiptSubtitle: "Choose email, phone, or on-screen QR receipt.",
    receiptMethodEmail: "Email",
    receiptMethodPhone: "Phone",
    receiptMethodScreen: "On Screen QR",
    receiptEmail: "Receipt Email",
    receiptEmailPlaceholder: "name@email.com",
    receiptPhone: "Receipt Phone",
    receiptPhonePlaceholder: "+359 888 123 456",
    receiptQrHint: "You can photograph the QR after payment.",
    checkoutFulfillmentTitle: "Fulfillment",
    checkoutFulfillmentSubtitle: "Choose pickup, table service, or delivery.",
    fulfillmentPickup: "Pick Up",
    fulfillmentTable: "Serve At Table",
    fulfillmentDelivery: "Deliver To Address",
    tableNumber: "Table Number",
    tableNumberPlaceholder: "e.g. 12",
    deliveryAddress: "Delivery Address",
    deliveryAddressPlaceholder: "Street, building, floor, note",
    checkoutPaymentTitle: "Payment Method",
    checkoutPaymentSubtitle: "Select how the customer will pay.",
    paymentMethodQr: "Pay By QR",
    paymentMethodTap: "Pay By Tap",
    paymentMethodLabel: "Payment method",
    next: "Next",
    back: "Back",
    payNow: "Pay Now",
    processingPayment: "Processing Payment",
    processingPaymentSubtitle: "Mock payment gateway is running.",
    orderPlaced: "Order placed",
    thankYou: "Thank You",
    receiptSentTo: "We sent your receipt to:",
    receiptSentToPhone: "We sent your receipt to phone:",
    receiptAvailableOnScreen: "Receipt ready on screen",
    receiptQrTitle: "Receipt QR",
    receiptQrSubtitle: "Scan this QR code to open your receipt.",
    orderNumber: "Order Number",
    newOrder: "New Order",
    kioskSettings: "Kiosk Settings",
    deviceControls: "Device Controls",
    settingsBody: "Use this screen to reset kiosk configuration on this device.",
    unconfigureKiosk: "Unconfigure Kiosk",
    unconfigureKioskTitle: "Unconfigure kiosk?",
    unconfigureKioskBody: "This will remove the device login and return to kiosk setup.",
    cancel: "Cancel",
    unconfigure: "Unconfigure",
    selectedVariant: "Selected variant",
    required: "Required",
    optional: "Optional",
    multiple: "Multiple",
    single: "Single",
    included: "Included",
    unitPrice: "Unit price",
    addToCart: "Add to Cart",
    customizerChoosePrefix: "Please choose",
    emailErrorInvalid: "Please enter a valid email for the receipt.",
    phoneErrorInvalid: "Please enter a valid phone number for the receipt.",
    tableErrorRequired: "Please enter a table number.",
    addressErrorRequired: "Please enter a delivery address.",
    defaultVariant: "Default",
  },
  bg: {
    venue: "Киоск",
    welcome: "Добре дошли",
    subtitle: "Докоснете, за да започнете поръчката си и да платите бързо.",
    chooseLanguage: "Изберете език",
    chooseTheme: "Изберете цветова тема",
    startOrder: "Започни поръчка",
    devicePreparing: "Подготвяне на киоска...",
    deviceLoginKicker: "Вход за устройство",
    deviceLoginTitle: "Конфигурирай този киоск",
    deviceLoginSubtitle: "Въведете кода на киоска, за да заредите конфигурацията на устройството.",
    kioskCodeLabel: "Код на киоска",
    kioskCodePlaceholder: "Пример: KIOSK-01",
    autoCodePrefix: "Автоматичен код от URL",
    loginKioskButton: "Вход в киоска",
    loginKioskButtonLoading: "Конфигуриране...",
    deviceErrorEnterCode: "Въведете кода за вход на киоска.",
    deviceErrorInvalidCode: "Невалиден код на киоска. Проверете и опитайте отново.",
    deviceErrorUnavailable: "Киоскът не може да се конфигурира в момента.",
    orderHere: "ПОРЪЧАЙТЕ ТУК",
    noImage: "Няма снимка",
    singleSize: "Един размер",
    add: "Добави",
    allCategory: "Всички",
    loadingProducts: "Зареждане на продукти...",
    loadProductsFailed: "Продуктите не могат да се заредят",
    retry: "Опитай отново",
    orderInSeconds: "Поръчай за секунди",
    browseSubtitle: "Изберете вариант на всяка карта, после допълнения след „Добави“.",
    startOver: "Начало",
    noProductsInCategory: "Няма продукти в тази категория.",
    itemInCart: "артикул в количката",
    itemsInCart: "артикула в количката",
    reviewOrder: "Преглед на поръчката",
    reviewSubtitle: "Прегледайте артикулите преди плащане.",
    itemNoteLabel: "Бележка за артикула",
    itemNotePlaceholder: "Без лук, повече сос, алергии...",
    orderNoteLabel: "Бележка за поръчката",
    orderNotePlaceholder: "Нещо допълнително за цялата поръчка?",
    emptyCart: "Количката е празна. Добавете продукти, за да продължите.",
    subtotal: "Междинна сума",
    service: "Обслужване",
    total: "Общо",
    checkoutReceiptTitle: "Бележка",
    checkoutReceiptSubtitle: "Изберете имейл, телефон или QR на екрана.",
    receiptMethodEmail: "Имейл",
    receiptMethodPhone: "Телефон",
    receiptMethodScreen: "QR на екрана",
    receiptEmail: "Имейл за бележка",
    receiptEmailPlaceholder: "name@email.com",
    receiptPhone: "Телефон за бележка",
    receiptPhonePlaceholder: "+359 888 123 456",
    receiptQrHint: "След плащане можете да снимате QR кода.",
    checkoutFulfillmentTitle: "Изпълнение",
    checkoutFulfillmentSubtitle: "Изберете вземане, сервиране на маса или доставка.",
    fulfillmentPickup: "Вземане",
    fulfillmentTable: "Сервирай на маса",
    fulfillmentDelivery: "Доставка до адрес",
    tableNumber: "Номер на маса",
    tableNumberPlaceholder: "напр. 12",
    deliveryAddress: "Адрес за доставка",
    deliveryAddressPlaceholder: "Улица, номер, етаж, бележка",
    checkoutPaymentTitle: "Метод на плащане",
    checkoutPaymentSubtitle: "Изберете как клиентът ще плати.",
    paymentMethodQr: "Плащане с QR",
    paymentMethodTap: "Плащане с докосване",
    paymentMethodLabel: "Метод на плащане",
    next: "Напред",
    back: "Назад",
    payNow: "Плати сега",
    processingPayment: "Обработка на плащането",
    processingPaymentSubtitle: "Работи демо платежен шлюз.",
    orderPlaced: "Поръчката е приета",
    thankYou: "Благодарим!",
    receiptSentTo: "Изпратихме бележката на:",
    receiptSentToPhone: "Изпратихме бележката на телефон:",
    receiptAvailableOnScreen: "Бележката е готова на екрана",
    receiptQrTitle: "QR за бележка",
    receiptQrSubtitle: "Сканирайте този QR код, за да отворите бележката.",
    orderNumber: "Номер на поръчка",
    newOrder: "Нова поръчка",
    kioskSettings: "Настройки на киоска",
    deviceControls: "Управление на устройството",
    settingsBody: "Оттук можете да нулирате конфигурацията на този киоск.",
    unconfigureKiosk: "Разконфигурирай киоска",
    unconfigureKioskTitle: "Разконфигуриране на киоска?",
    unconfigureKioskBody: "Това ще премахне входа на устройството и ще върне екрана за настройка.",
    cancel: "Отказ",
    unconfigure: "Разконфигурирай",
    selectedVariant: "Избран вариант",
    required: "Задължително",
    optional: "По избор",
    multiple: "Множество",
    single: "Единичен",
    included: "Включено",
    unitPrice: "Единична цена",
    addToCart: "Добави в количката",
    customizerChoosePrefix: "Изберете",
    emailErrorInvalid: "Въведете валиден имейл за бележката.",
    phoneErrorInvalid: "Въведете валиден телефон за бележката.",
    tableErrorRequired: "Въведете номер на маса.",
    addressErrorRequired: "Въведете адрес за доставка.",
    defaultVariant: "Стандартен",
  },
  it: {
    venue: "Chiosco Venue",
    welcome: "Benvenuto",
    subtitle: "Tocca per iniziare l'ordine e concludere il pagamento rapidamente.",
    chooseLanguage: "Scegli lingua",
    chooseTheme: "Scegli tema colori",
    startOrder: "Inizia ordine",
    devicePreparing: "Preparazione chiosco...",
    deviceLoginKicker: "Accesso Dispositivo",
    deviceLoginTitle: "Configura Questo Chiosco",
    deviceLoginSubtitle: "Inserisci il codice del chiosco per caricare la configurazione del dispositivo.",
    kioskCodeLabel: "Codice Chiosco",
    kioskCodePlaceholder: "Esempio: KIOSK-01",
    autoCodePrefix: "Codice automatico da URL",
    loginKioskButton: "Accedi al Chiosco",
    loginKioskButtonLoading: "Configurazione...",
    deviceErrorEnterCode: "Inserisci il codice di accesso del chiosco.",
    deviceErrorInvalidCode: "Codice chiosco non valido. Controlla e riprova.",
    deviceErrorUnavailable: "Impossibile configurare il chiosco in questo momento.",
    orderHere: "ORDINA QUI",
    noImage: "Nessuna immagine",
    singleSize: "Taglia unica",
    add: "Aggiungi",
    allCategory: "Tutti",
    loadingProducts: "Caricamento prodotti...",
    loadProductsFailed: "Impossibile caricare i prodotti",
    retry: "Riprova",
    orderInSeconds: "Ordina in pochi secondi",
    browseSubtitle: "Scegli la variante su ogni scheda, poi seleziona gli extra dopo Aggiungi.",
    startOver: "Ricomincia",
    noProductsInCategory: "Nessun prodotto in questa categoria.",
    itemInCart: "articolo nel carrello",
    itemsInCart: "articoli nel carrello",
    reviewOrder: "Rivedi ordine",
    reviewSubtitle: "Rivedi gli articoli prima del pagamento.",
    itemNoteLabel: "Nota Articolo",
    itemNotePlaceholder: "Senza cipolla, salsa extra, allergie...",
    orderNoteLabel: "Nota Ordine",
    orderNotePlaceholder: "Qualcos'altro per l'intero ordine?",
    emptyCart: "Il carrello è vuoto. Aggiungi prodotti per continuare.",
    subtotal: "Subtotale",
    service: "Servizio",
    total: "Totale",
    checkoutReceiptTitle: "Preferenza Ricevuta",
    checkoutReceiptSubtitle: "Scegli email, telefono o QR sullo schermo.",
    receiptMethodEmail: "Email",
    receiptMethodPhone: "Telefono",
    receiptMethodScreen: "QR a Schermo",
    receiptEmail: "Email ricevuta",
    receiptEmailPlaceholder: "nome@email.com",
    receiptPhone: "Telefono ricevuta",
    receiptPhonePlaceholder: "+39 333 123 4567",
    receiptQrHint: "Dopo il pagamento puoi fotografare il QR.",
    checkoutFulfillmentTitle: "Consegna Ordine",
    checkoutFulfillmentSubtitle: "Scegli ritiro, servizio al tavolo o consegna.",
    fulfillmentPickup: "Ritiro",
    fulfillmentTable: "Servi al Tavolo",
    fulfillmentDelivery: "Consegna a Indirizzo",
    tableNumber: "Numero Tavolo",
    tableNumberPlaceholder: "es. 12",
    deliveryAddress: "Indirizzo di Consegna",
    deliveryAddressPlaceholder: "Via, numero civico, piano, nota",
    checkoutPaymentTitle: "Metodo di Pagamento",
    checkoutPaymentSubtitle: "Seleziona come pagherà il cliente.",
    paymentMethodQr: "Paga con QR",
    paymentMethodTap: "Paga con Tap",
    paymentMethodLabel: "Metodo di pagamento",
    next: "Avanti",
    back: "Indietro",
    payNow: "Paga ora",
    processingPayment: "Elaborazione pagamento",
    processingPaymentSubtitle: "Gateway di pagamento simulato in esecuzione.",
    orderPlaced: "Ordine confermato",
    thankYou: "Grazie",
    receiptSentTo: "Abbiamo inviato la ricevuta a:",
    receiptSentToPhone: "Abbiamo inviato la ricevuta al telefono:",
    receiptAvailableOnScreen: "Ricevuta disponibile sullo schermo",
    receiptQrTitle: "QR Ricevuta",
    receiptQrSubtitle: "Scansiona questo codice QR per aprire la ricevuta.",
    orderNumber: "Numero ordine",
    newOrder: "Nuovo ordine",
    kioskSettings: "Impostazioni chiosco",
    deviceControls: "Controlli dispositivo",
    settingsBody: "Usa questa schermata per reimpostare la configurazione del chiosco.",
    unconfigureKiosk: "Rimuovi configurazione",
    unconfigureKioskTitle: "Rimuovere configurazione chiosco?",
    unconfigureKioskBody: "Questo rimuoverà il login dispositivo e tornerà alla configurazione.",
    cancel: "Annulla",
    unconfigure: "Rimuovi",
    selectedVariant: "Variante selezionata",
    required: "Obbligatorio",
    optional: "Opzionale",
    multiple: "Multipla",
    single: "Singola",
    included: "Incluso",
    unitPrice: "Prezzo unitario",
    addToCart: "Aggiungi al carrello",
    customizerChoosePrefix: "Scegli",
    emailErrorInvalid: "Inserisci un'email valida per la ricevuta.",
    phoneErrorInvalid: "Inserisci un numero di telefono valido per la ricevuta.",
    tableErrorRequired: "Inserisci un numero tavolo.",
    addressErrorRequired: "Inserisci un indirizzo di consegna.",
    defaultVariant: "Predefinita",
  },
};

function normalizeLocaleCode(value) {
  const raw = String(value || "en").trim();
  if (!raw) {
    return "en";
  }

  return raw.toLowerCase().split("-")[0];
}

function translateWithLocale(text, localeCode) {
  if (!text) {
    return "";
  }

  if (normalizeLocaleCode(localeCode) === "bg") {
    return text;
  }

  return i18n.t(text);
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeAllowedLanguages(value) {
  const normalized = Array.from(
    new Set(
      safeArray(value)
        .filter((lang) => typeof lang === "string")
        .map((lang) => normalizeLocaleCode(lang))
        .filter(Boolean)
    )
  );

  if (normalized.length > 0) {
    return normalized;
  }

  return Object.keys(WELCOME_COPY).map((code) => normalizeLocaleCode(code));
}

function normalizeDeviceCode(value) {
  return String(value || "").trim().toUpperCase();
}

function normalizeKioskConfig(value) {
  if (!value || typeof value !== "object") {
    return null;
  }

  const kioskName = String(value.kioskName || "").trim();
  const operatorId = String(value.operatorId || "").trim();
  const logoUrl = String(value.logoUrl || "").trim();
  const defaultPosId = String(value.defaultPosId || "").trim();
  const adUrl = String(value.adUrl || "").trim();

  if (!kioskName || !operatorId) {
    return null;
  }

  return {
    kioskName,
    operatorId,
    logoUrl: logoUrl || null,
    defaultPosId: defaultPosId || null,
    adUrl: adUrl || null,
  };
}

function normalizeCurrencyCode(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return null;
  }

  const upper = raw.toUpperCase();
  if (/^[A-Z]{3}$/.test(upper)) {
    return upper;
  }

  const symbolMap = {
    "€": "EUR",
    "$": "USD",
    "£": "GBP",
    "BGN": "BGN",
    "ЛВ": "BGN",
  };

  return symbolMap[upper] || null;
}

function getLocaleTag(localeCode) {
  const normalized = normalizeLocaleCode(localeCode);
  if (normalized === "bg") {
    return "bg-BG";
  }
  if (normalized === "it") {
    return "it-IT";
  }
  return "en-US";
}

function createMoneyFormatter(currencyCode, localeCode) {
  const safeCurrency = normalizeCurrencyCode(currencyCode) || "USD";
  const localeTag = getLocaleTag(localeCode);

  let formatter = null;
  try {
    formatter = new Intl.NumberFormat(localeTag, {
      style: "currency",
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch (_err) {
    formatter = null;
  }

  return (amount) => {
    const numericAmount = Number(amount || 0);
    if (formatter) {
      return formatter.format(numericAmount);
    }

    return `${safeCurrency} ${numericAmount.toFixed(2)}`;
  };
}

function buildOrderNumber() {
  const now = new Date();
  const hh = `${now.getHours()}`.padStart(2, "0");
  const mm = `${now.getMinutes()}`.padStart(2, "0");
  return `K-${hh}${mm}-${Math.floor(100 + Math.random() * 900)}`;
}

function isValidEmail(value) {
  if (!value) {
    return false;
  }

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isValidPhone(value) {
  if (!value) {
    return false;
  }

  const normalized = String(value).replace(/[^\d+]/g, "");
  return /^[+]?[0-9]{7,15}$/.test(normalized);
}

function hasOptionCustomization(product) {
  return safeArray(product.options).length > 0;
}

function getVariantName(product, variantIndex, fallbackName = "Default") {
  const variants = safeArray(product.variants);
  if (variants.length === 0) {
    return fallbackName;
  }

  return variants[variantIndex] || variants[0] || fallbackName;
}

function getVariantBasePrice(product, variantIndex) {
  const prices = safeArray(product.prices);
  const raw = prices[variantIndex] ?? prices[0] ?? 0;
  return Number(raw) || 0;
}

function getVariantAllergens(product, variantIndex) {
  const allergens = safeArray(product.allergens);
  const variantAllergens = safeArray(allergens[variantIndex]);
  if (variantAllergens.length > 0) {
    return variantAllergens.filter(Boolean);
  }

  return safeArray(allergens[0]).filter(Boolean);
}

function isMultiOption(optionGroup) {
  const optionType = String(optionGroup?.type || "").toLowerCase();
  return optionType === "add" || optionType === "multi" || optionType === "multiple";
}

function getChoicePrice(choice, variantIndex) {
  const prices = safeArray(choice?.prices);
  const raw = prices[variantIndex] ?? prices[0] ?? 0;
  return Number(raw) || 0;
}

function normalizeSelection(selection) {
  const normalized = {};

  Object.entries(selection || {}).forEach(([groupId, choiceIds]) => {
    const values = safeArray(choiceIds).filter(Boolean);
    if (values.length > 0) {
      normalized[groupId] = Array.from(new Set(values));
    }
  });

  return normalized;
}

function buildCartKey(productId, variantIndex, selection) {
  const optionParts = Object.entries(selection || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([groupId, choiceIds]) => `${groupId}:${[...choiceIds].sort().join(",")}`);

  return `${productId}|${variantIndex}|${optionParts.join("|")}`;
}

function getUnitPrice(product, variantIndex, selection) {
  let price = getVariantBasePrice(product, variantIndex);
  const optionGroups = safeArray(product.options);

  Object.entries(selection || {}).forEach(([groupId, choiceIds]) => {
    const group = optionGroups.find((entry) => entry.id === groupId);
    if (!group) {
      return;
    }

    safeArray(choiceIds).forEach((choiceId) => {
      const choice = safeArray(group.options).find((entry) => entry.id === choiceId);
      if (!choice) {
        return;
      }

      price += getChoicePrice(choice, variantIndex);
    });
  });

  return price;
}

function getSelectionSummary(product, selection) {
  const optionGroups = safeArray(product.options);
  const summary = [];

  Object.entries(selection || {}).forEach(([groupId, choiceIds]) => {
    const group = optionGroups.find((entry) => entry.id === groupId);
    if (!group) {
      return;
    }

    safeArray(choiceIds).forEach((choiceId) => {
      const choice = safeArray(group.options).find((entry) => entry.id === choiceId);
      if (choice) {
        summary.push({
          groupTitle: group.title,
          choiceTitle: choice.title,
        });
      }
    });
  });

  return summary;
}

// Convert the kiosk's normalized selection ({ groupId: [choiceIds] }) into the flat
// [optionId, choiceId, ...] pair array that OnlineOrderItemInput.options expects.
function buildOnlineItemOptions(product, selection) {
  const optionGroups = safeArray(product.options);
  const pairs = [];

  Object.entries(selection || {}).forEach(([groupId, choiceIds]) => {
    const group = optionGroups.find((entry) => entry.id === groupId);
    if (!group) {
      return;
    }

    safeArray(choiceIds).forEach((choiceId) => {
      const choice = safeArray(group.options).find((entry) => entry.id === choiceId);
      if (choice) {
        pairs.push(group.id, choice.id);
      }
    });
  });

  return pairs;
}

// Best-effort card charge on the webOS payment terminal via its Luna service. This is a
// no-op (resolves { skipped: true }) on any platform without the webOS service bridge, so
// it is safe to call everywhere. Final settlement/reconciliation still needs on-device
// verification on real hardware.
function requestWebOSTerminalPurchase(amountCents, reference) {
  return new Promise((resolve) => {
    try {
      if (typeof window === "undefined" || typeof window.WebOSServiceBridge === "undefined") {
        resolve({ ok: false, skipped: true });
        return;
      }

      const uri = "luna://com.refractionx.venue.webos26.payment.service/purchase";
      const bridge = new window.WebOSServiceBridge();
      let settled = false;

      const timer = setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        resolve({ ok: false, error: "timeout" });
      }, 120000);

      bridge.onservicecallback = (responseJson) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        try {
          const res = JSON.parse(responseJson);
          resolve({ ok: !!res && res.returnValue !== false && !res.errorText, response: res });
        } catch (err) {
          resolve({ ok: false, error: String(err) });
        }
      };

      bridge.call(uri, JSON.stringify({ amount: amountCents, currency: "EUR", reference }));
    } catch (err) {
      resolve({ ok: false, error: String(err) });
    }
  });
}

const ProductCard = React.memo(function ProductCard({
  product,
  index,
  quantityInCart,
  onAdd,
  onRemove,
  selectedVariantIndex,
  onSelectVariant,
  styles,
  copy,
  translate,
  formatPrice,
}) {
  const cardEnter = useRef(new Animated.Value(0)).current;
  const variants = safeArray(product.variants);
  const maxVariantIndex = Math.max(variants.length - 1, 0);
  const variantIndex = Math.min(Math.max(Number(selectedVariantIndex) || 0, 0), maxVariantIndex);
  const supportsMultipleVariants = variants.length > 1;
  const imageUrl = product.imageUrls && product.imageUrls[0]
    ? getProductImageUrl(product.imageUrls[0], { w: 960, h: 440, fit: 'cover', format: 'auto', q: 80 })
    : null;
  const allergens = getVariantAllergens(product, variantIndex);
  const basePrice = getVariantBasePrice(product, variantIndex);

  useEffect(() => {
    Animated.timing(cardEnter, {
      toValue: 1,
      duration: 420,
      delay: Math.min(45 * index, 380),
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [cardEnter, index]);

  return (
    <Animated.View
      style={[
        styles.productCard,
        {
          opacity: cardEnter,
          transform: [
            {
              translateY: cardEnter.interpolate({
                inputRange: [0, 1],
                outputRange: [20, 0],
              }),
            },
          ],
        },
      ]}
    >
      <View style={styles.imageFrame}>
        {imageUrl ? (
          <Image
            source={{ uri: imageUrl, cache: "default" }}
            resizeMode="cover"
            fadeDuration={0}
            progressiveRenderingEnabled
            style={styles.productImage}
          />
        ) : (
          <View style={[styles.productImage, styles.imagePlaceholder]}>
            <Text style={styles.imagePlaceholderText}>{copy.noImage}</Text>
          </View>
        )}

        {quantityInCart > 0 ? (
          <View style={styles.inCartBadge}>
            <Text style={styles.inCartBadgeText}>{quantityInCart}</Text>
          </View>
        ) : null}
      </View>

      <View style={styles.productMeta}>
        <Text style={styles.productTitle} numberOfLines={2}>
          {translate(product.title)}
        </Text>
        <Text style={styles.productSubtitle} numberOfLines={2}>
          {translate(product.subtitle || "")}
        </Text>

        <View style={styles.productInfoRow}>
          <Text style={styles.productPrice}>{formatPrice(basePrice)}</Text>
          {supportsMultipleVariants ? (
            <Text style={styles.productInfoHint}>{translate(getVariantName(product, variantIndex, copy.defaultVariant))}</Text>
          ) : (
            <Text style={styles.productInfoHint}>{copy.singleSize}</Text>
          )}
        </View>

        {supportsMultipleVariants ? (
          <View style={styles.variantRow}>
            {variants.map((variant, indexValue) => {
              const selected = variantIndex === indexValue;
              const price = getVariantBasePrice(product, indexValue);
              return (
                <Pressable
                  key={`${product.id}-${variant}-${indexValue}`}
                  style={({ pressed }) => [
                    styles.variantChip,
                    selected && styles.variantChipActive,
                    pressed && styles.buttonPressed,
                  ]}
                  onPress={() => onSelectVariant(product, indexValue)}
                >
                  <Text style={[styles.variantChipText, selected && styles.variantChipTextActive]}>
                    {translate(variant)}
                  </Text>
                  <Text style={[styles.variantChipPrice, selected && styles.variantChipPriceActive]}>
                    {formatPrice(price)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ) : null}

        {allergens.length > 0 ? (
          <View style={styles.allergenWrap}>
            <AllergenBadges allergens={allergens} size={12} style={styles.allergenDots} />
            <Text style={styles.allergenText} numberOfLines={2}>
              {allergens.map((allergen) => translate(allergen)).join(" · ")}
            </Text>
          </View>
        ) : null}

        <View style={styles.productActions}>
          <Pressable
            style={({ pressed }) => [
              styles.secondaryButton,
              styles.productActionButtonHalf,
              quantityInCart === 0 && styles.disabledButton,
              pressed && quantityInCart > 0 && styles.buttonPressed,
            ]}
            disabled={quantityInCart === 0}
            onPress={() => onRemove(product, variantIndex)}
          >
            <Text style={[styles.productActionSymbol, styles.productActionSymbolSecondary]}>-</Text>
          </Pressable>
          <Pressable
            style={({ pressed }) => [
              styles.primaryButton,
              styles.productActionButtonHalf,
              pressed && styles.buttonPressed,
            ]}
            onPress={() => onAdd(product, variantIndex)}
          >
            <Text style={[styles.productActionSymbol, styles.productActionSymbolPrimary]}>+</Text>
          </Pressable>
        </View>
      </View>
    </Animated.View>
  );
}, (previousProps, nextProps) => {
  return (
    previousProps.product === nextProps.product &&
    previousProps.index === nextProps.index &&
    previousProps.quantityInCart === nextProps.quantityInCart &&
    previousProps.selectedVariantIndex === nextProps.selectedVariantIndex &&
    previousProps.onAdd === nextProps.onAdd &&
    previousProps.onRemove === nextProps.onRemove &&
    previousProps.onSelectVariant === nextProps.onSelectVariant &&
    previousProps.styles === nextProps.styles &&
    previousProps.copy === nextProps.copy &&
    previousProps.translate === nextProps.translate &&
    previousProps.formatPrice === nextProps.formatPrice
  );
});

export default function KioskOrderScreen({ route }) {
  const { width } = useWindowDimensions();
  const routeParams = route?.params || {};
  const routeThemeName = String(routeParams.theme || DEFAULT_THEME).toLowerCase();
  const routeToken = String(routeParams.token || "").trim();
  const routeDeviceCode = useMemo(
    () => normalizeDeviceCode(routeParams.code || routeParams.deviceCode || routeParams.kioskCode),
    [routeParams.code, routeParams.deviceCode, routeParams.kioskCode]
  );
  const [activeThemeName, setActiveThemeName] = useState(
    THEMES[routeThemeName] ? routeThemeName : DEFAULT_THEME
  );
  const theme = THEMES[activeThemeName] || THEMES[DEFAULT_THEME];
  const welcomeAdHeight = useMemo(() => {
    const estimatedAvailableWidth = Math.max(width - 12, 360);
    const target = Math.round((estimatedAvailableWidth * 12) / 16) + 18;
    return Math.min(Math.max(target, WELCOME_AD_MIN_HEIGHT), WELCOME_AD_MAX_HEIGHT);
  }, [width]);
  const styles = useMemo(() => createStyles(theme, welcomeAdHeight), [theme, welcomeAdHeight]);
  const token = routeToken || "token";

  const [step, setStep] = useState("welcome");
  const [selectedCategory, setSelectedCategory] = useState(CATEGORY_ALL);
  const [selectedVariantsByProductId, setSelectedVariantsByProductId] = useState({});
  const [cart, setCart] = useState([]);
  const [orderNote, setOrderNote] = useState("");
  const [receiptMethod, setReceiptMethod] = useState(RECEIPT_METHOD_EMAIL);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [fulfillmentMethod, setFulfillmentMethod] = useState(FULFILLMENT_PICKUP);
  const [tableNumber, setTableNumber] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD_QR);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [tableError, setTableError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [uiLocale, setUiLocale] = useState(normalizeLocaleCode(i18n.getLocale() || "en"));
  const [availableLanguages, setAvailableLanguages] = useState(() => normalizeAllowedLanguages(i18n.getLanguages()));
  const [kioskConfig, setKioskConfig] = useState(null);
  const [kioskConfigLoaded, setKioskConfigLoaded] = useState(false);
  const [deviceCodeInput, setDeviceCodeInput] = useState(routeDeviceCode);
  const [deviceLoginError, setDeviceLoginError] = useState("");
  const [unconfigureConfirmVisible, setUnconfigureConfirmVisible] = useState(false);
  const [welcomeHeroLayout, setWelcomeHeroLayout] = useState({ y: 0, height: 0 });
  const [welcomeCardLayout, setWelcomeCardLayout] = useState({ y: 0, height: 0 });
  const [orderHereLayoutHeight, setOrderHereLayoutHeight] = useState(0);

  const [customizerVisible, setCustomizerVisible] = useState(false);
  const [customizerProduct, setCustomizerProduct] = useState(null);
  const [customizerVariantIndex, setCustomizerVariantIndex] = useState(0);
  const [customizerOptions, setCustomizerOptions] = useState({});
  const [customizerError, setCustomizerError] = useState("");

  const transitionValue = useRef(new Animated.Value(0)).current;
  const blobWave = useRef(new Animated.Value(0)).current;
  const cartPulse = useRef(new Animated.Value(1)).current;
  const paySpin = useRef(new Animated.Value(0)).current;
  const orderHereArrowAnim = useRef(new Animated.Value(0)).current;
  const thankYouConfettiAnim = useRef(new Animated.Value(0)).current;

  const blobLoopRef = useRef(null);
  const spinLoopRef = useRef(null);
  const confettiLoopRef = useRef(null);
  const payTimerRef = useRef(null);
  const paymentSubmittedRef = useRef(false);
  const thankYouResetTimerRef = useRef(null);
  const orderHereArrowLoopRef = useRef(null);
  const autoLoginCodeRef = useRef("");
  const secretSettingsProgressRef = useRef(0);
  const secretSettingsTimeoutRef = useRef(null);

  const [runKioskDeviceLogin, { loading: deviceLoginLoading }] = useLazyQuery(
    KIOSK_DEVICE_LOGIN_QUERY,
    { fetchPolicy: "network-only" }
  );

  const { loading, error, data, refetch } = useQuery(KIOSK_PRODUCTS_QUERY, {
    variables: { token },
    skip: !kioskConfig,
    fetchPolicy: "cache-and-network",
  });

  const [createOnlineOrder] = useMutation(CREATE_ONLINE_ORDER);

  const numColumns = width >= 1050 ? 2 : 1;
  const currencyCode = useMemo(
    () => normalizeCurrencyCode(data?.currency) || normalizeCurrencyCode(routeParams.currency) || "USD",
    [data?.currency, routeParams.currency]
  );
  const themeNames = useMemo(() => Object.keys(THEMES), []);
  const copy = WELCOME_COPY[uiLocale] || WELCOME_COPY.en;
  const translate = useCallback((text) => translateWithLocale(text, uiLocale), [uiLocale]);
  const formatPrice = useMemo(() => createMoneyFormatter(currencyCode, uiLocale), [currencyCode, uiLocale]);
  const kioskName = kioskConfig?.kioskName || copy.venue;
  const kioskLogoUrl = kioskConfig?.logoUrl || null;
  const kioskLogoImageUrl = kioskLogoUrl ? getProductImageUrl(kioskLogoUrl) : null;
  const kioskDefaultPosId = kioskConfig?.defaultPosId || null;
  const kioskAdUrl = String(kioskConfig?.adUrl || "").trim();
  const paymentMethodLabel = paymentMethod === PAYMENT_METHOD_TAP ? copy.paymentMethodTap : copy.paymentMethodQr;
  const receiptQrValue = useMemo(() => {
    const safeOrderNumber = orderNumber || "pending";
    const contact = receiptMethod === RECEIPT_METHOD_EMAIL
      ? email.trim()
      : receiptMethod === RECEIPT_METHOD_PHONE
        ? phone.trim()
        : "onscreen";
    const destination = fulfillmentMethod === FULFILLMENT_TABLE
      ? tableNumber.trim()
      : fulfillmentMethod === FULFILLMENT_DELIVERY
        ? deliveryAddress.trim()
        : "pickup";
    return `venue://kiosk-receipt/${encodeURIComponent(safeOrderNumber)}?receipt=${encodeURIComponent(receiptMethod)}&contact=${encodeURIComponent(contact)}&fulfillment=${encodeURIComponent(fulfillmentMethod)}&destination=${encodeURIComponent(destination)}&payment=${encodeURIComponent(paymentMethod)}&ordernote=${encodeURIComponent(orderNote.trim())}`;
  }, [deliveryAddress, email, fulfillmentMethod, orderNote, orderNumber, paymentMethod, phone, receiptMethod, tableNumber]);
  const thankYouConfettiPieces = useMemo(() => {
    return Array.from({ length: 28 }, (_, index) => {
      const leftPercent = 4 + ((index * 19) % 92);
      const size = 8 + ((index * 7) % 6);
      const fallDistance = 420 + (index % 7) * 64;
      const sway = (index % 2 === 0 ? 1 : -1) * (14 + (index % 6) * 6);
      const rotateStart = ((index * 31) % 260) - 130;
      const rotateEnd = rotateStart + (index % 2 === 0 ? 260 : -260);
      const delay = (index % 12) / 12;
      return {
        id: `confetti-${index}`,
        leftPercent,
        size,
        fallDistance,
        sway,
        rotateStart,
        rotateEnd,
        delay,
        color: THANK_YOU_CONFETTI_COLORS[index % THANK_YOU_CONFETTI_COLORS.length],
      };
    });
  }, []);
  const orderHereTop = useMemo(() => {
    const heroBottom = welcomeHeroLayout.y + welcomeHeroLayout.height;
    const cardTop = welcomeCardLayout.y;
    if (orderHereLayoutHeight <= 0 || cardTop <= heroBottom + 2) {
      return null;
    }

    const gap = cardTop - heroBottom;
    return heroBottom + Math.max((gap - orderHereLayoutHeight) / 2, 0);
  }, [orderHereLayoutHeight, welcomeCardLayout.y, welcomeHeroLayout.height, welcomeHeroLayout.y]);

  const persistKioskConfig = useCallback(async (nextConfig, code) => {
    try {
      await AsyncStorage.setItem(
        KIOSK_DEVICE_CONFIG_STORAGE_KEY,
        JSON.stringify(nextConfig)
      );
      if (code) {
        await AsyncStorage.setItem(KIOSK_DEVICE_CODE_STORAGE_KEY, normalizeDeviceCode(code));
      }
    } catch (_storageError) {
      // Non-fatal: kiosk can still continue using in-memory config.
    }
  }, []);

  const loginWithDeviceCode = useCallback(async (rawCode) => {
    const normalizedCode = normalizeDeviceCode(rawCode);
    if (!normalizedCode) {
      setDeviceLoginError(copy.deviceErrorEnterCode);
      return false;
    }

    setDeviceLoginError("");

    try {
      const result = await runKioskDeviceLogin({
        variables: { code: normalizedCode },
      });
      const resolved = normalizeKioskConfig(result?.data?.kioskLogin);
      if (!resolved) {
        setDeviceLoginError(copy.deviceErrorInvalidCode);
        return false;
      }

      setKioskConfig(resolved);
      await persistKioskConfig(resolved, normalizedCode);
      setDeviceCodeInput(normalizedCode);
      return true;
    } catch (_error) {
      setDeviceLoginError(copy.deviceErrorUnavailable);
      return false;
    }
  }, [copy.deviceErrorEnterCode, copy.deviceErrorInvalidCode, copy.deviceErrorUnavailable, persistKioskConfig, runKioskDeviceLogin]);

  const clearDeviceConfiguration = useCallback(async () => {
    try {
      await AsyncStorage.multiRemove([
        KIOSK_DEVICE_CONFIG_STORAGE_KEY,
        KIOSK_DEVICE_CODE_STORAGE_KEY,
      ]);
    } catch (_storageError) {
      // Ignore removal failures; local state still resets.
    }

    setKioskConfig(null);
    setDeviceLoginError("");
    setDeviceCodeInput("");
    setStep("welcome");
    setCart([]);
    setOrderNote("");
    setSelectedVariantsByProductId({});
    setReceiptMethod(RECEIPT_METHOD_EMAIL);
    setEmail("");
    setPhone("");
    setFulfillmentMethod(FULFILLMENT_PICKUP);
    setTableNumber("");
    setDeliveryAddress("");
    setPaymentMethod(PAYMENT_METHOD_QR);
    setEmailError("");
    setPhoneError("");
    setTableError("");
    setAddressError("");
    setOrderNumber("");
    setSelectedCategory(CATEGORY_ALL);
    setUnconfigureConfirmVisible(false);
  }, []);

  const resetSecretSettingsProgress = useCallback(() => {
    secretSettingsProgressRef.current = 0;
    if (secretSettingsTimeoutRef.current) {
      clearTimeout(secretSettingsTimeoutRef.current);
      secretSettingsTimeoutRef.current = null;
    }
  }, []);

  const armSecretSettingsTimeout = useCallback(() => {
    if (secretSettingsTimeoutRef.current) {
      clearTimeout(secretSettingsTimeoutRef.current);
    }

    secretSettingsTimeoutRef.current = setTimeout(() => {
      secretSettingsTimeoutRef.current = null;
      secretSettingsProgressRef.current = 0;
    }, SECRET_SETTINGS_TIMEOUT_MS);
  }, []);

  const registerSecretGesture = useCallback((gestureCode) => {
    const currentIndex = secretSettingsProgressRef.current;
    const expectedGesture = SECRET_SETTINGS_SEQUENCE[currentIndex];
    const firstGesture = SECRET_SETTINGS_SEQUENCE[0];

    if (gestureCode === expectedGesture) {
      const nextIndex = currentIndex + 1;
      if (nextIndex >= SECRET_SETTINGS_SEQUENCE.length) {
        resetSecretSettingsProgress();
        setStep("kioskSettings");
        return;
      }

      secretSettingsProgressRef.current = nextIndex;
      armSecretSettingsTimeout();
      return;
    }

    if (gestureCode === firstGesture) {
      secretSettingsProgressRef.current = 1;
      armSecretSettingsTimeout();
      return;
    }

    resetSecretSettingsProgress();
  }, [armSecretSettingsTimeout, resetSecretSettingsProgress]);

  useEffect(() => {
    const normalizedRouteTheme = THEMES[routeThemeName] ? routeThemeName : DEFAULT_THEME;
    setActiveThemeName((previous) => (previous === normalizedRouteTheme ? previous : normalizedRouteTheme));
  }, [routeThemeName]);

  useEffect(() => {
    resetSecretSettingsProgress();
  }, [step, resetSecretSettingsProgress]);

  useEffect(() => {
    let cancelled = false;

    const loadStoredKioskConfig = async () => {
      let nextConfig = null;
      let storedCode = "";

      try {
        const rawConfig = await AsyncStorage.getItem(KIOSK_DEVICE_CONFIG_STORAGE_KEY);
        if (rawConfig) {
          nextConfig = normalizeKioskConfig(JSON.parse(rawConfig));
        }
      } catch (_readError) {
        nextConfig = null;
      }

      try {
        storedCode = normalizeDeviceCode(await AsyncStorage.getItem(KIOSK_DEVICE_CODE_STORAGE_KEY));
      } catch (_readError) {
        storedCode = "";
      }

      if (cancelled) {
        return;
      }

      if (nextConfig) {
        setKioskConfig(nextConfig);
      }

      setDeviceCodeInput(routeDeviceCode || storedCode || "");
      setKioskConfigLoaded(true);
    };

    loadStoredKioskConfig();

    return () => {
      cancelled = true;
    };
  }, [routeDeviceCode]);

  useEffect(() => {
    if (!kioskConfigLoaded || kioskConfig || !routeDeviceCode) {
      return;
    }

    if (autoLoginCodeRef.current === routeDeviceCode) {
      return;
    }

    autoLoginCodeRef.current = routeDeviceCode;
    setDeviceCodeInput(routeDeviceCode);
    void loginWithDeviceCode(routeDeviceCode);
  }, [kioskConfigLoaded, kioskConfig, routeDeviceCode, loginWithDeviceCode]);

  useEffect(() => {
    let cancelled = false;

    const updateLanguages = () => {
      const allowed = normalizeAllowedLanguages(i18n.getLanguages());
      const currentLocale = normalizeLocaleCode(i18n.getLocale() || "en");
      const nextLocale = allowed.includes(currentLocale) ? currentLocale : allowed[0] || "en";
      if (nextLocale !== currentLocale) {
        void i18n.setLocale(nextLocale);
      }
      if (!cancelled) {
        setAvailableLanguages(allowed);
        setUiLocale(nextLocale);
      }
    };

    updateLanguages();
    const timer = setTimeout(updateLanguages, 1800);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  const categories = useMemo(() => {
    const remote = safeArray(data?.categories)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return [
      {
        id: CATEGORY_ALL,
        name: copy.allCategory,
        order: -1,
      },
      ...remote,
    ];
  }, [copy.allCategory, data]);

  const products = useMemo(() => {
    const list = safeArray(data?.products)
      .filter((product) => product.available !== false && product.published !== false)
      .slice();

    list.sort((a, b) => {
      const orderDiff = (a.order ?? 9999) - (b.order ?? 9999);
      if (orderDiff !== 0) {
        return orderDiff;
      }

      return translate(a.title || "").localeCompare(translate(b.title || ""));
    });

    return list;
  }, [data, translate]);

  const filteredProducts = useMemo(() => {
    if (selectedCategory === CATEGORY_ALL) {
      return products;
    }

    return products.filter((product) => product.category === selectedCategory);
  }, [products, selectedCategory]);

  const normalizeVariantIndex = useCallback((product, variantIndex) => {
    const variantCount = Math.max(safeArray(product?.variants).length, 1);
    const parsedIndex = Number(variantIndex);
    const safeIndex = Number.isFinite(parsedIndex) ? Math.floor(parsedIndex) : 0;
    return Math.min(Math.max(safeIndex, 0), variantCount - 1);
  }, []);

  useEffect(() => {
    setSelectedVariantsByProductId((previous) => {
      let changed = false;
      const productById = new Map(products.map((product) => [product.id, product]));
      const next = {};

      Object.entries(previous).forEach(([productId, variantIndex]) => {
        const product = productById.get(productId);
        if (!product) {
          changed = true;
          return;
        }

        const normalized = normalizeVariantIndex(product, variantIndex);
        if (normalized > 0) {
          next[productId] = normalized;
        } else {
          changed = true;
        }

        if (normalized !== variantIndex) {
          changed = true;
        }
      });

      if (!changed && Object.keys(next).length === Object.keys(previous).length) {
        return previous;
      }

      return next;
    });
  }, [normalizeVariantIndex, products]);

  const getSelectedVariantIndex = useCallback(
    (product) => normalizeVariantIndex(product, selectedVariantsByProductId[product.id] ?? 0),
    [normalizeVariantIndex, selectedVariantsByProductId]
  );

  const selectVariantForProduct = useCallback(
    (product, variantIndex) => {
      const normalized = normalizeVariantIndex(product, variantIndex);

      setSelectedVariantsByProductId((previous) => {
        const current = previous[product.id] ?? 0;
        if (current === normalized) {
          return previous;
        }

        if (normalized === 0) {
          if (!(product.id in previous)) {
            return previous;
          }

          const next = { ...previous };
          delete next[product.id];
          return next;
        }

        return {
          ...previous,
          [product.id]: normalized,
        };
      });
    },
    [normalizeVariantIndex]
  );

  const quantityByProduct = useMemo(() => {
    return cart.reduce((acc, item) => {
      acc[item.product.id] = (acc[item.product.id] || 0) + item.quantity;
      return acc;
    }, {});
  }, [cart]);

  const itemCount = useMemo(
    () => cart.reduce((sum, item) => sum + item.quantity, 0),
    [cart]
  );

  const subtotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      const unitPrice = getUnitPrice(item.product, item.variantIndex, item.selectedOptions);
      return sum + unitPrice * item.quantity;
    }, 0);
  }, [cart]);

  const serviceFee = useMemo(() => subtotal * KIOSK_SERVICE_FEE_RATE, [subtotal]);
  const total = subtotal + serviceFee;

  const customizerPrice = useMemo(() => {
    if (!customizerProduct) {
      return 0;
    }

    return getUnitPrice(customizerProduct, customizerVariantIndex, customizerOptions);
  }, [customizerProduct, customizerVariantIndex, customizerOptions]);

  const customizerAllergens = useMemo(() => {
    if (!customizerProduct) {
      return [];
    }

    return getVariantAllergens(customizerProduct, customizerVariantIndex);
  }, [customizerProduct, customizerVariantIndex]);

  useEffect(() => {
    transitionValue.setValue(0);
    Animated.timing(transitionValue, {
      toValue: 1,
      duration: 340,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [step, transitionValue]);

  useEffect(() => {
    blobLoopRef.current = Animated.loop(
      Animated.sequence([
        Animated.timing(blobWave, {
          toValue: 1,
          duration: 4200,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(blobWave, {
          toValue: 0,
          duration: 4200,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ])
    );

    blobLoopRef.current.start();

    return () => {
      if (blobLoopRef.current) {
        blobLoopRef.current.stop();
        blobLoopRef.current = null;
      }
    };
  }, [blobWave]);

  useEffect(() => {
    orderHereArrowLoopRef.current = Animated.loop(
      Animated.sequence([
        Animated.timing(orderHereArrowAnim, {
          toValue: 1,
          duration: 520,
          easing: Easing.out(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(orderHereArrowAnim, {
          toValue: 0,
          duration: 520,
          easing: Easing.in(Easing.quad),
          useNativeDriver: true,
        }),
      ])
    );

    orderHereArrowLoopRef.current.start();

    return () => {
      if (orderHereArrowLoopRef.current) {
        orderHereArrowLoopRef.current.stop();
        orderHereArrowLoopRef.current = null;
      }
    };
  }, [orderHereArrowAnim]);

  useEffect(() => {
    if (itemCount === 0) {
      return;
    }

    Animated.sequence([
      Animated.spring(cartPulse, {
        toValue: 1.03,
        useNativeDriver: true,
        friction: 5,
        tension: 130,
      }),
      Animated.spring(cartPulse, {
        toValue: 1,
        useNativeDriver: true,
        friction: 6,
        tension: 120,
      }),
    ]).start();
  }, [cartPulse, itemCount]);

  const processKioskPayment = useCallback(async () => {
    if (cart.length === 0) {
      return { ok: false, error: "empty_cart" };
    }

    const channel = fulfillmentMethod === FULFILLMENT_DELIVERY ? "delivery" : "pickup";
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    const commentParts = [orderNote.trim()].filter(Boolean);

    // Tap: authorize on the physical terminal FIRST, before persisting the order. This way a
    // decline, timeout, or missing terminal never leaves an orphaned unpaid order, and a
    // retry can't create duplicates. With no service fee, total === subtotal === the order's
    // orderGross. A missing terminal bridge (skipped) is a hard failure for a real tap sale.
    if (paymentMethod === PAYMENT_METHOD_TAP) {
      const amountCents = Math.round(total * 100);
      const reference = `KIOSK-${Date.now()}`;
      const terminal = await requestWebOSTerminalPurchase(amountCents, reference);
      if (!terminal || terminal.ok !== true) {
        return { ok: false, error: (terminal && terminal.error) || (terminal && terminal.skipped ? "terminal_unavailable" : "terminal_declined") };
      }
      commentParts.push(`tap-ref:${reference}`);
    }

    const input = {
      location: null,
      channel,
      // pay_online keeps the in-person kiosk flow frictionless: the backend skips the
      // emailed/SMS confirmation-code step that every other paymentMethod requires.
      paymentMethod: "pay_online",
      customerName: "",
      customerEmail: receiptMethod === RECEIPT_METHOD_EMAIL ? trimmedEmail : "",
      customerPhone: receiptMethod === RECEIPT_METHOD_PHONE ? trimmedPhone : "",
      city: "",
      street: fulfillmentMethod === FULFILLMENT_DELIVERY ? deliveryAddress.trim() : "",
      comment: commentParts.join(" | "),
      deliveryFee: 0,
      items: cart.map((item) => ({
        productId: item.product.id,
        variant: item.variantName,
        quantity: item.quantity,
        options: buildOnlineItemOptions(item.product, item.selectedOptions),
        capabilities: [],
        comment: item.note || "",
      })),
    };

    try {
      const result = await createOnlineOrder({ variables: { input } });
      const order = result?.data?.createOnlineOrder;
      if (!order) {
        return { ok: false, error: "no_order" };
      }
      return { ok: true, orderNumber: order.orderNumber || order.id };
    } catch (mutationError) {
      return { ok: false, error: mutationError?.message || "request_failed" };
    }
  }, [
    cart,
    createOnlineOrder,
    deliveryAddress,
    email,
    fulfillmentMethod,
    orderNote,
    paymentMethod,
    phone,
    receiptMethod,
    total,
  ]);

  useEffect(() => {
    if (step !== "paying") {
      paymentSubmittedRef.current = false;
      paySpin.setValue(0);
      if (spinLoopRef.current) {
        spinLoopRef.current.stop();
        spinLoopRef.current = null;
      }
      return undefined;
    }

    spinLoopRef.current = Animated.loop(
      Animated.timing(paySpin, {
        toValue: 1,
        duration: 900,
        easing: Easing.linear,
        useNativeDriver: true,
      })
    );

    spinLoopRef.current.start();

    // Submit the real order (and, for tap, charge the terminal). Only advance to the
    // thank-you screen once the order is actually persisted; surface failures instead of
    // faking success. The ref guard guarantees a single submission per paying entry.
    let active = true;
    if (!paymentSubmittedRef.current) {
      paymentSubmittedRef.current = true;
      processKioskPayment().then((result) => {
        if (!active) {
          return;
        }
        if (result && result.ok) {
          setOrderNumber(result.orderNumber);
          setStep("thankyou");
        } else {
          setStep("payment");
          if (!result || result.error !== "empty_cart") {
            Alert.alert(
              translate("Payment could not be completed"),
              translate("Please try again or ask a staff member for help.")
            );
          }
        }
      });
    }

    return () => {
      active = false;
      if (spinLoopRef.current) {
        spinLoopRef.current.stop();
        spinLoopRef.current = null;
      }
    };
  }, [paySpin, step, processKioskPayment, translate]);

  useEffect(() => {
    return () => {
      if (payTimerRef.current) {
        clearTimeout(payTimerRef.current);
      }
      if (thankYouResetTimerRef.current) {
        clearTimeout(thankYouResetTimerRef.current);
      }
      if (confettiLoopRef.current) {
        confettiLoopRef.current.stop();
        confettiLoopRef.current = null;
      }
      if (secretSettingsTimeoutRef.current) {
        clearTimeout(secretSettingsTimeoutRef.current);
      }
      if (orderHereArrowLoopRef.current) {
        orderHereArrowLoopRef.current.stop();
        orderHereArrowLoopRef.current = null;
      }
    };
  }, []);

  const addConfiguredProduct = useCallback((product, variantIndex, selectedOptions) => {
    const normalizedSelection = normalizeSelection(selectedOptions);
    const key = buildCartKey(product.id, variantIndex, normalizedSelection);

    setCart((previous) => {
      const existingIndex = previous.findIndex((entry) => entry.key === key);
      if (existingIndex === -1) {
        return [
          ...previous,
          {
            key,
            product,
            variantIndex,
            variantName: getVariantName(product, variantIndex, copy.defaultVariant),
            selectedOptions: normalizedSelection,
            note: "",
            quantity: 1,
          },
        ];
      }

      return previous.map((entry, index) =>
        index === existingIndex ? { ...entry, quantity: entry.quantity + 1 } : entry
      );
    });
  }, [copy.defaultVariant]);

  const removeCartItem = useCallback((itemKey) => {
    setCart((previous) => {
      const existing = previous.find((entry) => entry.key === itemKey);
      if (!existing) {
        return previous;
      }

      if (existing.quantity <= 1) {
        return previous.filter((entry) => entry.key !== itemKey);
      }

      return previous.map((entry) =>
        entry.key === itemKey ? { ...entry, quantity: entry.quantity - 1 } : entry
      );
    });
  }, []);

  const updateCartItemNote = useCallback((itemKey, nextNote) => {
    setCart((previous) => previous.map((entry) => (
      entry.key === itemKey ? { ...entry, note: nextNote } : entry
    )));
  }, []);

  const openCustomizer = useCallback((product, variantIndex = 0) => {
    const normalized = normalizeVariantIndex(product, variantIndex);
    setCustomizerProduct(product);
    setCustomizerVariantIndex(normalized);
    setCustomizerOptions({});
    setCustomizerError("");
    setCustomizerVisible(true);
  }, [normalizeVariantIndex]);

  const quickAdd = useCallback(
    (product, variantIndex = 0) => {
      const normalized = normalizeVariantIndex(product, variantIndex);
      if (hasOptionCustomization(product)) {
        openCustomizer(product, normalized);
        return;
      }

      addConfiguredProduct(product, normalized, {});
    },
    [addConfiguredProduct, normalizeVariantIndex, openCustomizer]
  );

  const quickRemove = useCallback((product, variantIndex = 0) => {
    const normalized = normalizeVariantIndex(product, variantIndex);
    setCart((previous) => {
      let targetIndex = -1;

      for (let index = previous.length - 1; index >= 0; index -= 1) {
        const entry = previous[index];
        if (entry.product.id === product.id && entry.variantIndex === normalized) {
          targetIndex = index;
          break;
        }
      }

      if (targetIndex === -1) {
        for (let index = previous.length - 1; index >= 0; index -= 1) {
          const entry = previous[index];
          if (entry.product.id === product.id) {
            targetIndex = index;
            break;
          }
        }
      }

      if (targetIndex === -1) {
        return previous;
      }

      const target = previous[targetIndex];
      if (target.quantity <= 1) {
        return previous.filter((_, index) => index !== targetIndex);
      }

      return previous.map((entry, index) => (
        index === targetIndex ? { ...entry, quantity: entry.quantity - 1 } : entry
      ));
    });
  }, [normalizeVariantIndex]);

  const toggleCustomizerChoice = useCallback((group, choiceId) => {
    setCustomizerOptions((previous) => {
      const current = safeArray(previous[group.id]);
      const selected = current.includes(choiceId);
      const multi = isMultiOption(group);

      if (multi) {
        if (selected) {
          const reduced = current.filter((id) => id !== choiceId);
          if (reduced.length === 0) {
            const next = { ...previous };
            delete next[group.id];
            return next;
          }

          return {
            ...previous,
            [group.id]: reduced,
          };
        }

        return {
          ...previous,
          [group.id]: [...current, choiceId],
        };
      }

      if (selected && !group.required) {
        const next = { ...previous };
        delete next[group.id];
        return next;
      }

      return {
        ...previous,
        [group.id]: [choiceId],
      };
    });
  }, []);

  const closeCustomizer = useCallback(() => {
    setCustomizerVisible(false);
    setCustomizerProduct(null);
    setCustomizerVariantIndex(0);
    setCustomizerOptions({});
    setCustomizerError("");
  }, []);

  const confirmCustomizer = useCallback(() => {
    if (!customizerProduct) {
      return;
    }

    const missingRequired = safeArray(customizerProduct.options).filter(
      (group) => group.required && safeArray(customizerOptions[group.id]).length === 0
    );

    if (missingRequired.length > 0) {
      setCustomizerError(
        `${copy.customizerChoosePrefix}: ${missingRequired
          .map((group) => translate(group.title))
          .join(", ")}`
      );
      return;
    }

    addConfiguredProduct(customizerProduct, customizerVariantIndex, customizerOptions);
    closeCustomizer();
  }, [
    addConfiguredProduct,
    closeCustomizer,
    customizerOptions,
    customizerProduct,
    customizerVariantIndex,
    copy.customizerChoosePrefix,
    translate,
  ]);

  const validateReceiptSelection = useCallback(() => {
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    let hasError = false;
    setEmailError("");
    setPhoneError("");

    if (receiptMethod === RECEIPT_METHOD_EMAIL && !isValidEmail(trimmedEmail)) {
      setEmailError(copy.emailErrorInvalid);
      hasError = true;
    }

    if (receiptMethod === RECEIPT_METHOD_PHONE && !isValidPhone(trimmedPhone)) {
      setPhoneError(copy.phoneErrorInvalid);
      hasError = true;
    }

    return !hasError;
  }, [copy.emailErrorInvalid, copy.phoneErrorInvalid, email, phone, receiptMethod]);

  const validateFulfillmentSelection = useCallback(() => {
    const trimmedTable = tableNumber.trim();
    const trimmedAddress = deliveryAddress.trim();
    let hasError = false;
    setTableError("");
    setAddressError("");

    if (fulfillmentMethod === FULFILLMENT_TABLE && !trimmedTable) {
      setTableError(copy.tableErrorRequired);
      hasError = true;
    }

    if (fulfillmentMethod === FULFILLMENT_DELIVERY && !trimmedAddress) {
      setAddressError(copy.addressErrorRequired);
      hasError = true;
    }

    return !hasError;
  }, [copy.addressErrorRequired, copy.tableErrorRequired, deliveryAddress, fulfillmentMethod, tableNumber]);

  const continueToReceipt = useCallback(() => {
    if (cart.length === 0) {
      return;
    }

    setStep("receipt");
  }, [cart.length]);

  const continueToFulfillment = useCallback(() => {
    if (!validateReceiptSelection()) {
      return;
    }

    setStep("fulfillment");
  }, [validateReceiptSelection]);

  const continueToPayment = useCallback(() => {
    if (!validateFulfillmentSelection()) {
      return;
    }

    setStep("payment");
  }, [validateFulfillmentSelection]);

  const startPayment = useCallback(() => {
    if (cart.length === 0) {
      return;
    }

    const receiptValid = validateReceiptSelection();
    const fulfillmentValid = validateFulfillmentSelection();
    if (!receiptValid || !fulfillmentValid) {
      return;
    }

    setStep("paying");
  }, [cart.length, validateFulfillmentSelection, validateReceiptSelection]);

  const resetFlow = useCallback(() => {
    setStep("welcome");
    setCart([]);
    setOrderNote("");
    setSelectedVariantsByProductId({});
    setReceiptMethod(RECEIPT_METHOD_EMAIL);
    setEmail("");
    setPhone("");
    setFulfillmentMethod(FULFILLMENT_PICKUP);
    setTableNumber("");
    setDeliveryAddress("");
    setPaymentMethod(PAYMENT_METHOD_QR);
    setEmailError("");
    setPhoneError("");
    setTableError("");
    setAddressError("");
    setOrderNumber("");
    setSelectedCategory(CATEGORY_ALL);
  }, []);

  useEffect(() => {
    if (step !== "thankyou") {
      thankYouConfettiAnim.setValue(0);
      if (confettiLoopRef.current) {
        confettiLoopRef.current.stop();
        confettiLoopRef.current = null;
      }
      if (thankYouResetTimerRef.current) {
        clearTimeout(thankYouResetTimerRef.current);
        thankYouResetTimerRef.current = null;
      }
      return;
    }

    thankYouConfettiAnim.setValue(0);
    confettiLoopRef.current = Animated.loop(
      Animated.sequence([
        Animated.timing(thankYouConfettiAnim, {
          toValue: 1,
          duration: 2200,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
        Animated.timing(thankYouConfettiAnim, {
          toValue: 0,
          duration: 0,
          useNativeDriver: true,
        }),
      ])
    );
    confettiLoopRef.current.start();

    if (thankYouResetTimerRef.current) {
      clearTimeout(thankYouResetTimerRef.current);
    }
    thankYouResetTimerRef.current = setTimeout(() => {
      thankYouResetTimerRef.current = null;
      resetFlow();
    }, THANK_YOU_AUTO_RESET_MS);

    return () => {
      if (confettiLoopRef.current) {
        confettiLoopRef.current.stop();
        confettiLoopRef.current = null;
      }
      if (thankYouResetTimerRef.current) {
        clearTimeout(thankYouResetTimerRef.current);
        thankYouResetTimerRef.current = null;
      }
    };
  }, [resetFlow, step, thankYouConfettiAnim]);

  const closeUnconfigureConfirm = useCallback(() => {
    setUnconfigureConfirmVisible(false);
  }, []);

  const openUnconfigureConfirm = useCallback(() => {
    setUnconfigureConfirmVisible(true);
  }, []);

  const confirmUnconfigureKiosk = useCallback(() => {
    setUnconfigureConfirmVisible(false);
    void clearDeviceConfiguration();
  }, [clearDeviceConfiguration]);

  const changeLanguage = useCallback(async (language) => {
    const normalized = normalizeLocaleCode(language);
    await i18n.setLocale(normalized);
    setUiLocale(normalized);
  }, []);

  const languageLabel = useCallback((code) => {
    return LANGUAGE_LABELS[code] || code.toUpperCase();
  }, []);

  const themeLabel = useCallback((code) => {
    const labels = THEME_LABELS[code];
    if (!labels || typeof labels !== "object") {
      return code;
    }

    return labels[uiLocale] || labels.en || code;
  }, [uiLocale]);

  const changeTheme = useCallback((themeName) => {
    if (!THEMES[themeName]) {
      return;
    }

    setActiveThemeName(themeName);
  }, []);

  const handleWelcomeHeroLayout = useCallback((event) => {
    const layout = event?.nativeEvent?.layout;
    if (!layout) {
      return;
    }

    setWelcomeHeroLayout((previous) => {
      if (previous.y === layout.y && previous.height === layout.height) {
        return previous;
      }

      return { y: layout.y, height: layout.height };
    });
  }, []);

  const handleWelcomeCardLayout = useCallback((event) => {
    const layout = event?.nativeEvent?.layout;
    if (!layout) {
      return;
    }

    setWelcomeCardLayout((previous) => {
      if (previous.y === layout.y && previous.height === layout.height) {
        return previous;
      }

      return { y: layout.y, height: layout.height };
    });
  }, []);

  const handleOrderHereLayout = useCallback((event) => {
    const layout = event?.nativeEvent?.layout;
    if (!layout || !layout.height) {
      return;
    }

    setOrderHereLayoutHeight((previous) => (previous === layout.height ? previous : layout.height));
  }, []);

  const renderThemeSelector = (compact = false) => {
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={compact ? styles.themeScrollCompact : styles.themeScroll}
        contentContainerStyle={compact ? styles.themeScrollContentCompact : styles.themeScrollContent}
      >
        {themeNames.map((themeName) => {
          const palette = THEMES[themeName];
          const selected = activeThemeName === themeName;

          return (
            <Pressable
              key={themeName}
              style={({ pressed }) => [
                compact ? styles.themeChipCompact : styles.themeChip,
                selected && {
                  borderColor: palette.accent,
                  backgroundColor: palette.accentSoft,
                },
                pressed && styles.buttonPressed,
              ]}
              onPress={() => changeTheme(themeName)}
            >
              <View style={styles.themeSwatchRow}>
                <View style={[styles.themeSwatch, { backgroundColor: palette.accent }]} />
                <View style={[styles.themeSwatch, { backgroundColor: palette.accentSecondary }]} />
                <View style={[styles.themeSwatch, { backgroundColor: palette.panelSoft, borderColor: palette.line }]} />
              </View>
              <Text style={[compact ? styles.themeChipTextCompact : styles.themeChipText, selected && { color: palette.text }]}>
                {themeLabel(themeName)}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    );
  };

  const productKeyExtractor = useCallback((item) => item.id, []);

  const renderProductItem = useCallback(
    ({ item, index }) => {
      const quantityInCart = quantityByProduct[item.id] || 0;
      const selectedVariantIndex = getSelectedVariantIndex(item);
      return (
        <View style={[styles.productGridCell, numColumns > 1 ? styles.productGridCellHalf : styles.productGridCellFull]}>
          <ProductCard
            product={item}
            index={index}
            quantityInCart={quantityInCart}
            selectedVariantIndex={selectedVariantIndex}
            onSelectVariant={selectVariantForProduct}
            onAdd={quickAdd}
            onRemove={quickRemove}
            styles={styles}
            copy={copy}
            translate={translate}
            formatPrice={formatPrice}
          />
        </View>
      );
    },
    [
      formatPrice,
      getSelectedVariantIndex,
      numColumns,
      quantityByProduct,
      quickAdd,
      quickRemove,
      selectVariantForProduct,
      styles,
      copy,
      translate,
    ]
  );

  const renderDeviceLogin = () => {
    if (!kioskConfigLoaded) {
      return (
        <View style={styles.loadingState}>
          <ActivityIndicator size="large" color={theme.accent} />
          <Text style={styles.loadingText}>{copy.devicePreparing}</Text>
        </View>
      );
    }

    return (
      <View style={styles.centerStep}>
        <View style={styles.welcomeCard}>
          <Text style={styles.kicker}>{copy.deviceLoginKicker}</Text>
          <Text style={styles.welcomeTitle}>{copy.deviceLoginTitle}</Text>
          <Text style={styles.welcomeSubtitle}>
            {copy.deviceLoginSubtitle}
          </Text>
          <Text style={styles.languageTitle}>{copy.chooseLanguage}</Text>
          <View style={styles.languageGrid}>
            {availableLanguages.map((language) => {
              const selected = uiLocale === language;
              return (
                <Pressable
                  key={`device-login-lang-${language}`}
                  style={({ pressed }) => [
                    styles.languageChip,
                    selected && styles.languageChipActive,
                    pressed && styles.buttonPressed,
                  ]}
                  onPress={() => {
                    void changeLanguage(language);
                  }}
                >
                  <Text style={styles.languageFlag}>{LANGUAGE_FLAGS[language] || "🌐"}</Text>
                  <Text style={[styles.languageChipText, selected && styles.languageChipTextActive]}>
                    {languageLabel(language)}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <Text style={styles.emailLabel}>{copy.kioskCodeLabel}</Text>
          <TextInput
            value={deviceCodeInput}
            onChangeText={(value) => {
              setDeviceCodeInput(normalizeDeviceCode(value));
              if (deviceLoginError) {
                setDeviceLoginError("");
              }
            }}
            placeholder={copy.kioskCodePlaceholder}
            placeholderTextColor={theme.textMuted}
            autoCapitalize="characters"
            autoCorrect={false}
            style={[styles.emailInput, deviceLoginError ? styles.emailInputError : null]}
          />
          {deviceLoginError ? <Text style={styles.emailError}>{deviceLoginError}</Text> : null}
          {routeDeviceCode ? (
            <Text style={styles.deviceHint}>
              {copy.autoCodePrefix}: {routeDeviceCode}
            </Text>
          ) : null}

          <Pressable
            style={({ pressed }) => [
              styles.primaryButton,
              styles.primaryButtonFull,
              styles.deviceLoginPrimaryButton,
              deviceLoginLoading && styles.disabledButton,
              pressed && !deviceLoginLoading && styles.buttonPressed,
            ]}
            disabled={deviceLoginLoading}
            onPress={() => {
              void loginWithDeviceCode(deviceCodeInput);
            }}
          >
            <Text style={styles.primaryButtonText}>
              {deviceLoginLoading ? copy.loginKioskButtonLoading : copy.loginKioskButton}
            </Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderWelcome = () => {
    return (
      <View style={styles.centerStep}>
        <View style={[styles.welcomeLayout, !kioskAdUrl && styles.welcomeLayoutNoAd]}>
          <View style={styles.welcomeHero} onLayout={handleWelcomeHeroLayout}>
            {kioskLogoImageUrl ? (
              <Image source={{ uri: kioskLogoImageUrl }} style={styles.kioskLogo} resizeMode="contain" />
            ) : null}
            <Text style={styles.welcomeHeroTitle}>{copy.welcome}</Text>
          </View>
          <View
            style={[
              styles.orderHereWrap,
              orderHereTop === null
                ? (kioskLogoImageUrl ? styles.orderHereWrapWithLogo : styles.orderHereWrapNoLogo)
                : { top: orderHereTop },
            ]}
            onLayout={handleOrderHereLayout}
          >
            <Text style={styles.orderHereText}>{copy.orderHere}</Text>
            <Animated.Text
              style={[
                styles.orderHereArrow,
                {
                  opacity: orderHereArrowAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.72, 1],
                  }),
                  transform: [
                    {
                      translateY: orderHereArrowAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-8, 8],
                      }),
                    },
                    {
                      scale: orderHereArrowAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [1, 1.14],
                      }),
                    },
                  ],
                },
              ]}
            >
              ↓
            </Animated.Text>
          </View>
          <View
            style={[styles.welcomeCard, kioskLogoImageUrl ? styles.welcomeCardWithLogoHero : styles.welcomeCardNoLogoHero]}
            onLayout={handleWelcomeCardLayout}
          >
            <Text style={styles.kicker}>{kioskName}</Text>
            <Text style={styles.welcomeSubtitle}>
              {copy.subtitle}
            </Text>
            {kioskDefaultPosId ? (
              <Text style={styles.deviceMetaText}>
                POS {kioskDefaultPosId}
              </Text>
            ) : null}

            <Text style={styles.languageTitle}>{copy.chooseLanguage}</Text>
            <View style={styles.languageGrid}>
              {availableLanguages.map((language) => {
                const selected = uiLocale === language;
                return (
                  <Pressable
                    key={language}
                    style={({ pressed }) => [
                      styles.languageChip,
                      selected && styles.languageChipActive,
                      pressed && styles.buttonPressed,
                    ]}
                    onPress={() => {
                      void changeLanguage(language);
                    }}
                  >
                    <Text style={styles.languageFlag}>{LANGUAGE_FLAGS[language] || "🌐"}</Text>
                    <Text style={[styles.languageChipText, selected && styles.languageChipTextActive]}>
                      {languageLabel(language)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <Text style={styles.themeTitle}>{copy.chooseTheme}</Text>
            {renderThemeSelector()}

            <Pressable
              style={({ pressed }) => [styles.primaryButton, styles.primaryButtonFull, pressed && styles.buttonPressed]}
              onPress={() => setStep("browse")}
            >
              <Text style={styles.primaryButtonText}>{copy.startOrder}</Text>
            </Pressable>
          </View>

          {kioskAdUrl ? (
            <View style={styles.welcomeAdWrap}>
              <View style={styles.welcomeAdFrame}>
                {Platform.OS === "web" ? (
                  <iframe
                    src={kioskAdUrl}
                    title="kiosk-ad"
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "0",
                      display: "block",
                    }}
                  />
                ) : NativeWebView ? (
                  <NativeWebView
                    source={{ uri: kioskAdUrl }}
                    style={styles.welcomeAdWebView}
                    originWhitelist={["*"]}
                    javaScriptEnabled
                    domStorageEnabled
                    mixedContentMode="always"
                    mediaPlaybackRequiresUserAction={false}
                    allowsInlineMediaPlayback
                    scrollEnabled={false}
                    bounces={false}
                    overScrollMode="never"
                    setSupportMultipleWindows={false}
                  />
                ) : null}
              </View>
            </View>
          ) : null}
        </View>
      </View>
    );
  };

  const renderBrowse = () => {
    if (loading && !data) {
      return (
        <View style={styles.loadingState}>
          <ActivityIndicator size="large" color={theme.accent} />
          <Text style={styles.loadingText}>{copy.loadingProducts}</Text>
        </View>
      );
    }

    if (error) {
      return (
        <View style={styles.loadingState}>
          <Text style={styles.errorTitle}>{copy.loadProductsFailed}</Text>
          <Text style={styles.errorBody}>{error.message}</Text>
          <Pressable style={({ pressed }) => [styles.primaryButton, styles.primaryButtonFull, pressed && styles.buttonPressed]} onPress={() => refetch()}>
            <Text style={styles.primaryButtonText}>{copy.retry}</Text>
          </Pressable>
        </View>
      );
    }

    return (
      <View style={styles.flexFill}>
        <View style={styles.headerWrap}>
          {kioskLogoImageUrl ? (
            <Image source={{ uri: kioskLogoImageUrl }} style={styles.kioskLogoCompact} resizeMode="contain" />
          ) : null}
          <Text style={styles.kicker}>{kioskName}</Text>
          <Text style={styles.title}>{copy.orderInSeconds}</Text>
          <Text style={styles.subtitle}>
            {copy.browseSubtitle}
          </Text>
          {renderThemeSelector(true)}
          <Pressable
            style={({ pressed }) => [styles.secondaryButton, styles.secondaryButtonFull, pressed && styles.buttonPressed]}
            onPress={resetFlow}
          >
            <Text style={styles.secondaryButtonText}>{copy.startOver}</Text>
          </Pressable>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.categoryScroll}
          contentContainerStyle={styles.categoryScrollContent}
        >
          {categories.map((category) => {
            const active = selectedCategory === category.id;
            return (
              <Pressable
                key={category.id}
                style={({ pressed }) => [
                  styles.categoryChip,
                  active && styles.categoryChipActive,
                  pressed && styles.buttonPressed,
                ]}
                onPress={() => setSelectedCategory(category.id)}
              >
                <Text style={[styles.categoryChipText, active && styles.categoryChipTextActive]}>
                  {translate(category.name)}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>

        <FlatList
          key={`kiosk-products-${numColumns}`}
          data={filteredProducts}
          numColumns={numColumns}
          keyExtractor={productKeyExtractor}
          columnWrapperStyle={numColumns > 1 ? styles.productGridRow : undefined}
          contentContainerStyle={styles.productGridContent}
          renderItem={renderProductItem}
          removeClippedSubviews
          initialNumToRender={PRODUCT_LIST_INITIAL_RENDER}
          maxToRenderPerBatch={PRODUCT_LIST_BATCH_SIZE}
          updateCellsBatchingPeriod={16}
          windowSize={PRODUCT_LIST_WINDOW_SIZE}
          ListEmptyComponent={
            <View style={styles.loadingState}>
              <Text style={styles.loadingText}>{copy.noProductsInCategory}</Text>
            </View>
          }
        />

        <Animated.View style={[styles.cartBar, { transform: [{ scale: cartPulse }] }]}>
          <View>
            <Text style={styles.cartMeta}>
              {itemCount} {itemCount === 1 ? copy.itemInCart : copy.itemsInCart}
            </Text>
            <Text style={styles.cartTotal}>{formatPrice(total)}</Text>
          </View>

          <Pressable
            style={({ pressed }) => [
              styles.primaryButton,
              styles.primaryButtonFixed,
              itemCount === 0 && styles.disabledButton,
              pressed && itemCount > 0 && styles.buttonPressed,
            ]}
            disabled={itemCount === 0}
            onPress={() => setStep("review")}
          >
            <Text style={styles.primaryButtonText}>{copy.reviewOrder}</Text>
          </Pressable>
        </Animated.View>
      </View>
    );
  };

  const renderOrderTotals = () => {
    return (
      <View style={styles.receiptBox}>
        <View style={styles.receiptRow}>
          <Text style={styles.receiptLabel}>{copy.subtotal}</Text>
          <Text style={styles.receiptValue}>{formatPrice(subtotal)}</Text>
        </View>
        {serviceFee > 0 ? (
          <View style={styles.receiptRow}>
            <Text style={styles.receiptLabel}>{copy.service}</Text>
            <Text style={styles.receiptValue}>{formatPrice(serviceFee)}</Text>
          </View>
        ) : null}
        <View style={styles.receiptDivider} />
        <View style={styles.receiptRow}>
          <Text style={styles.receiptTotalLabel}>{copy.total}</Text>
          <Text style={styles.receiptTotalValue}>{formatPrice(total)}</Text>
        </View>
      </View>
    );
  };

  const renderReceiptSection = () => {
    return (
      <View style={styles.checkoutSection}>
        <Text style={styles.checkoutSectionTitle}>{copy.checkoutReceiptTitle}</Text>
        <Text style={styles.checkoutSectionSubtitle}>{copy.checkoutReceiptSubtitle}</Text>
        <View style={styles.checkoutOptionRow}>
          <Pressable
            style={({ pressed }) => [
              styles.checkoutOptionChip,
              receiptMethod === RECEIPT_METHOD_EMAIL && styles.checkoutOptionChipActive,
              pressed && styles.buttonPressed,
            ]}
            onPress={() => {
              setReceiptMethod(RECEIPT_METHOD_EMAIL);
              setPhoneError("");
            }}
          >
            <MaterialCommunityIcons
              name="email-outline"
              size={40}
              color={receiptMethod === RECEIPT_METHOD_EMAIL ? theme.accent : theme.textMuted}
              style={styles.checkoutOptionIcon}
            />
            <Text style={[styles.checkoutOptionText, receiptMethod === RECEIPT_METHOD_EMAIL && styles.checkoutOptionTextActive]}>
              {copy.receiptMethodEmail}
            </Text>
          </Pressable>
          <Pressable
            style={({ pressed }) => [
              styles.checkoutOptionChip,
              receiptMethod === RECEIPT_METHOD_PHONE && styles.checkoutOptionChipActive,
              pressed && styles.buttonPressed,
            ]}
            onPress={() => {
              setReceiptMethod(RECEIPT_METHOD_PHONE);
              setEmailError("");
            }}
          >
            <MaterialCommunityIcons
              name="phone-outline"
              size={40}
              color={receiptMethod === RECEIPT_METHOD_PHONE ? theme.accent : theme.textMuted}
              style={styles.checkoutOptionIcon}
            />
            <Text style={[styles.checkoutOptionText, receiptMethod === RECEIPT_METHOD_PHONE && styles.checkoutOptionTextActive]}>
              {copy.receiptMethodPhone}
            </Text>
          </Pressable>
          <Pressable
            style={({ pressed }) => [
              styles.checkoutOptionChip,
              receiptMethod === RECEIPT_METHOD_SCREEN && styles.checkoutOptionChipActive,
              pressed && styles.buttonPressed,
            ]}
            onPress={() => {
              setReceiptMethod(RECEIPT_METHOD_SCREEN);
              setEmailError("");
              setPhoneError("");
            }}
          >
            <MaterialCommunityIcons
              name="qrcode-scan"
              size={40}
              color={receiptMethod === RECEIPT_METHOD_SCREEN ? theme.accent : theme.textMuted}
              style={styles.checkoutOptionIcon}
            />
            <Text style={[styles.checkoutOptionText, receiptMethod === RECEIPT_METHOD_SCREEN && styles.checkoutOptionTextActive]}>
              {copy.receiptMethodScreen}
            </Text>
          </Pressable>
        </View>

        {receiptMethod === RECEIPT_METHOD_EMAIL ? (
          <View style={styles.emailBlock}>
            <Text style={styles.emailLabel}>{copy.receiptEmail}</Text>
            <TextInput
              value={email}
              onChangeText={(value) => {
                setEmail(value);
                if (emailError) {
                  setEmailError("");
                }
              }}
              placeholder={copy.receiptEmailPlaceholder}
              placeholderTextColor={theme.textMuted}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              style={[styles.emailInput, emailError ? styles.emailInputError : null]}
            />
            {emailError ? <Text style={styles.emailError}>{emailError}</Text> : null}
          </View>
        ) : null}

        {receiptMethod === RECEIPT_METHOD_PHONE ? (
          <View style={styles.emailBlock}>
            <Text style={styles.emailLabel}>{copy.receiptPhone}</Text>
            <TextInput
              value={phone}
              onChangeText={(value) => {
                setPhone(value);
                if (phoneError) {
                  setPhoneError("");
                }
              }}
              placeholder={copy.receiptPhonePlaceholder}
              placeholderTextColor={theme.textMuted}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="phone-pad"
              style={[styles.emailInput, phoneError ? styles.emailInputError : null]}
            />
            {phoneError ? <Text style={styles.emailError}>{phoneError}</Text> : null}
          </View>
        ) : null}

        {receiptMethod === RECEIPT_METHOD_SCREEN ? (
          <Text style={styles.checkoutHint}>{copy.receiptQrHint}</Text>
        ) : null}
      </View>
    );
  };

  const renderFulfillmentSection = () => {
    return (
      <View style={styles.checkoutSection}>
        <Text style={styles.checkoutSectionTitle}>{copy.checkoutFulfillmentTitle}</Text>
        <Text style={styles.checkoutSectionSubtitle}>{copy.checkoutFulfillmentSubtitle}</Text>
        <View style={styles.checkoutOptionRow}>
          <Pressable
            style={({ pressed }) => [
              styles.checkoutOptionChip,
              fulfillmentMethod === FULFILLMENT_PICKUP && styles.checkoutOptionChipActive,
              pressed && styles.buttonPressed,
            ]}
            onPress={() => {
              setFulfillmentMethod(FULFILLMENT_PICKUP);
              setTableError("");
              setAddressError("");
            }}
          >
            <MaterialCommunityIcons
              name="bag-carry-on-check"
              size={40}
              color={fulfillmentMethod === FULFILLMENT_PICKUP ? theme.accent : theme.textMuted}
              style={styles.checkoutOptionIcon}
            />
            <Text style={[styles.checkoutOptionText, fulfillmentMethod === FULFILLMENT_PICKUP && styles.checkoutOptionTextActive]}>
              {copy.fulfillmentPickup}
            </Text>
          </Pressable>
          <Pressable
            style={({ pressed }) => [
              styles.checkoutOptionChip,
              fulfillmentMethod === FULFILLMENT_TABLE && styles.checkoutOptionChipActive,
              pressed && styles.buttonPressed,
            ]}
            onPress={() => {
              setFulfillmentMethod(FULFILLMENT_TABLE);
              setAddressError("");
            }}
          >
            <MaterialCommunityIcons
              name="table-chair"
              size={40}
              color={fulfillmentMethod === FULFILLMENT_TABLE ? theme.accent : theme.textMuted}
              style={styles.checkoutOptionIcon}
            />
            <Text style={[styles.checkoutOptionText, fulfillmentMethod === FULFILLMENT_TABLE && styles.checkoutOptionTextActive]}>
              {copy.fulfillmentTable}
            </Text>
          </Pressable>
          <Pressable
            style={({ pressed }) => [
              styles.checkoutOptionChip,
              fulfillmentMethod === FULFILLMENT_DELIVERY && styles.checkoutOptionChipActive,
              pressed && styles.buttonPressed,
            ]}
            onPress={() => {
              setFulfillmentMethod(FULFILLMENT_DELIVERY);
              setTableError("");
            }}
          >
            <MaterialCommunityIcons
              name="bike-fast"
              size={40}
              color={fulfillmentMethod === FULFILLMENT_DELIVERY ? theme.accent : theme.textMuted}
              style={styles.checkoutOptionIcon}
            />
            <Text style={[styles.checkoutOptionText, fulfillmentMethod === FULFILLMENT_DELIVERY && styles.checkoutOptionTextActive]}>
              {copy.fulfillmentDelivery}
            </Text>
          </Pressable>
        </View>

        {fulfillmentMethod === FULFILLMENT_TABLE ? (
          <View style={styles.emailBlock}>
            <Text style={styles.emailLabel}>{copy.tableNumber}</Text>
            <TextInput
              value={tableNumber}
              onChangeText={(value) => {
                setTableNumber(value);
                if (tableError) {
                  setTableError("");
                }
              }}
              placeholder={copy.tableNumberPlaceholder}
              placeholderTextColor={theme.textMuted}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="number-pad"
              style={[styles.emailInput, tableError ? styles.emailInputError : null]}
            />
            {tableError ? <Text style={styles.emailError}>{tableError}</Text> : null}
          </View>
        ) : null}

        {fulfillmentMethod === FULFILLMENT_DELIVERY ? (
          <View style={styles.emailBlock}>
            <Text style={styles.emailLabel}>{copy.deliveryAddress}</Text>
            <TextInput
              value={deliveryAddress}
              onChangeText={(value) => {
                setDeliveryAddress(value);
                if (addressError) {
                  setAddressError("");
                }
              }}
              placeholder={copy.deliveryAddressPlaceholder}
              placeholderTextColor={theme.textMuted}
              autoCapitalize="sentences"
              autoCorrect={false}
              multiline
              numberOfLines={3}
              style={[styles.emailInput, styles.checkoutMultilineInput, addressError ? styles.emailInputError : null]}
            />
            {addressError ? <Text style={styles.emailError}>{addressError}</Text> : null}
          </View>
        ) : null}
      </View>
    );
  };

  const renderPaymentSection = () => {
    return (
      <View style={styles.checkoutSection}>
        <Text style={styles.checkoutSectionTitle}>{copy.checkoutPaymentTitle}</Text>
        <Text style={styles.checkoutSectionSubtitle}>{copy.checkoutPaymentSubtitle}</Text>
        <View style={styles.checkoutOptionRow}>
          <Pressable
            style={({ pressed }) => [
              styles.checkoutOptionChip,
              styles.checkoutOptionChipWide,
              paymentMethod === PAYMENT_METHOD_QR && styles.checkoutOptionChipActive,
              pressed && styles.buttonPressed,
            ]}
            onPress={() => setPaymentMethod(PAYMENT_METHOD_QR)}
          >
            <MaterialCommunityIcons
              name="qrcode"
              size={44}
              color={paymentMethod === PAYMENT_METHOD_QR ? theme.accent : theme.textMuted}
              style={styles.checkoutOptionIcon}
            />
            <Text style={[styles.checkoutOptionText, paymentMethod === PAYMENT_METHOD_QR && styles.checkoutOptionTextActive]}>
              {copy.paymentMethodQr}
            </Text>
          </Pressable>
          <Pressable
            style={({ pressed }) => [
              styles.checkoutOptionChip,
              styles.checkoutOptionChipWide,
              paymentMethod === PAYMENT_METHOD_TAP && styles.checkoutOptionChipActive,
              pressed && styles.buttonPressed,
            ]}
            onPress={() => setPaymentMethod(PAYMENT_METHOD_TAP)}
          >
            <MaterialCommunityIcons
              name="credit-card-wireless-outline"
              size={44}
              color={paymentMethod === PAYMENT_METHOD_TAP ? theme.accent : theme.textMuted}
              style={styles.checkoutOptionIcon}
            />
            <Text style={[styles.checkoutOptionText, paymentMethod === PAYMENT_METHOD_TAP && styles.checkoutOptionTextActive]}>
              {copy.paymentMethodTap}
            </Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderReview = () => {
    return (
      <View style={styles.flexFill}>
        <View style={styles.headerWrapCompact}>
          <Text style={styles.title}>{copy.reviewOrder}</Text>
          <Text style={styles.subtitle}>{copy.reviewSubtitle}</Text>
        </View>

        <ScrollView style={styles.reviewScroll} contentContainerStyle={styles.reviewContent}>
          {cart.length === 0 ? (
            <Text style={styles.emptyText}>{copy.emptyCart}</Text>
          ) : null}

          {cart.map((item) => {
            const unitPrice = getUnitPrice(item.product, item.variantIndex, item.selectedOptions);
            const lineTotal = unitPrice * item.quantity;
            const selectedSummary = getSelectionSummary(item.product, item.selectedOptions);
            const allergens = getVariantAllergens(item.product, item.variantIndex);

            return (
              <View key={item.key} style={styles.reviewItem}>
                <View style={styles.reviewMain}>
                  <Text style={styles.reviewTitle}>{translate(item.product.title)}</Text>
                  <Text style={styles.reviewVariant}>
                    {translate(getVariantName(item.product, item.variantIndex, copy.defaultVariant))}
                  </Text>

                  {selectedSummary.length > 0 ? (
                    <Text style={styles.reviewOptions}>
                      {selectedSummary
                        .map((entry) => `${translate(entry.groupTitle)}: ${translate(entry.choiceTitle)}`)
                        .join(" · ")}
                    </Text>
                  ) : null}

                  {allergens.length > 0 ? (
                    <View style={styles.reviewAllergenWrap}>
                      <AllergenBadges allergens={allergens} size={11} />
                      <Text style={styles.reviewAllergenText} numberOfLines={2}>
                        {allergens.map((allergen) => translate(allergen)).join(" · ")}
                      </Text>
                    </View>
                  ) : null}

                  <View style={styles.itemNoteBlock}>
                    <Text style={styles.itemNoteLabel}>{copy.itemNoteLabel}</Text>
                    <TextInput
                      value={item.note || ""}
                      onChangeText={(value) => updateCartItemNote(item.key, value)}
                      placeholder={copy.itemNotePlaceholder}
                      placeholderTextColor={theme.textMuted}
                      autoCapitalize="sentences"
                      autoCorrect={false}
                      multiline
                      numberOfLines={2}
                      style={styles.itemNoteInput}
                    />
                  </View>
                </View>

                <View style={styles.reviewControls}>
                  <Pressable
                    style={({ pressed }) => [styles.qtyControl, pressed && styles.buttonPressed]}
                    onPress={() => removeCartItem(item.key)}
                  >
                    <Text style={styles.qtyControlText}>-</Text>
                  </Pressable>

                  <Text style={styles.qtyText}>{item.quantity}</Text>

                  <Pressable
                    style={({ pressed }) => [styles.qtyControl, pressed && styles.buttonPressed]}
                    onPress={() => addConfiguredProduct(item.product, item.variantIndex, item.selectedOptions)}
                  >
                    <Text style={styles.qtyControlText}>+</Text>
                  </Pressable>
                </View>

                <Text style={styles.reviewLineTotal}>{formatPrice(lineTotal)}</Text>
              </View>
            );
          })}

          {renderOrderTotals()}

          <View style={styles.orderNoteBlock}>
            <Text style={styles.emailLabel}>{copy.orderNoteLabel}</Text>
            <TextInput
              value={orderNote}
              onChangeText={setOrderNote}
              placeholder={copy.orderNotePlaceholder}
              placeholderTextColor={theme.textMuted}
              autoCapitalize="sentences"
              autoCorrect={false}
              multiline
              numberOfLines={3}
              style={[styles.emailInput, styles.checkoutMultilineInput]}
            />
          </View>
        </ScrollView>

        <View style={styles.reviewStartOverWrap}>
          <Pressable
            style={({ pressed }) => [styles.secondaryButton, styles.secondaryButtonFull, pressed && styles.buttonPressed]}
            onPress={resetFlow}
          >
            <Text style={styles.secondaryButtonText}>{copy.startOver}</Text>
          </Pressable>
        </View>

        <View style={styles.reviewFooter}>
          <Pressable style={({ pressed }) => [styles.secondaryButton, styles.secondaryButtonWide, pressed && styles.buttonPressed]} onPress={() => setStep("browse")}>
            <Text style={styles.secondaryButtonText}>{copy.back}</Text>
          </Pressable>

          <Pressable
            style={({ pressed }) => [
              styles.primaryButton,
              styles.primaryButtonWide,
              cart.length === 0 && styles.disabledButton,
              pressed && cart.length > 0 && styles.buttonPressed,
            ]}
            disabled={cart.length === 0}
            onPress={continueToReceipt}
          >
            <Text style={styles.primaryButtonText}>{copy.next}</Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderReceiptStep = () => {
    return (
      <View style={styles.flexFill}>
        <View style={styles.headerWrapCompact}>
          <Text style={styles.title}>{copy.checkoutReceiptTitle}</Text>
          <Text style={styles.subtitle}>{copy.checkoutReceiptSubtitle}</Text>
        </View>

        <ScrollView style={styles.reviewScroll} contentContainerStyle={styles.reviewContent}>
          {renderOrderTotals()}
          {renderReceiptSection()}
        </ScrollView>

        <View style={styles.reviewStartOverWrap}>
          <Pressable
            style={({ pressed }) => [styles.secondaryButton, styles.secondaryButtonFull, pressed && styles.buttonPressed]}
            onPress={resetFlow}
          >
            <Text style={styles.secondaryButtonText}>{copy.startOver}</Text>
          </Pressable>
        </View>

        <View style={styles.reviewFooter}>
          <Pressable style={({ pressed }) => [styles.secondaryButton, styles.secondaryButtonWide, pressed && styles.buttonPressed]} onPress={() => setStep("review")}>
            <Text style={styles.secondaryButtonText}>{copy.back}</Text>
          </Pressable>

          <Pressable
            style={({ pressed }) => [styles.primaryButton, styles.primaryButtonWide, pressed && styles.buttonPressed]}
            onPress={continueToFulfillment}
          >
            <Text style={styles.primaryButtonText}>{copy.next}</Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderFulfillmentStep = () => {
    return (
      <View style={styles.flexFill}>
        <View style={styles.headerWrapCompact}>
          <Text style={styles.title}>{copy.checkoutFulfillmentTitle}</Text>
          <Text style={styles.subtitle}>{copy.checkoutFulfillmentSubtitle}</Text>
        </View>

        <ScrollView style={styles.reviewScroll} contentContainerStyle={styles.reviewContent}>
          {renderOrderTotals()}
          {renderFulfillmentSection()}
        </ScrollView>

        <View style={styles.reviewStartOverWrap}>
          <Pressable
            style={({ pressed }) => [styles.secondaryButton, styles.secondaryButtonFull, pressed && styles.buttonPressed]}
            onPress={resetFlow}
          >
            <Text style={styles.secondaryButtonText}>{copy.startOver}</Text>
          </Pressable>
        </View>

        <View style={styles.reviewFooter}>
          <Pressable style={({ pressed }) => [styles.secondaryButton, styles.secondaryButtonWide, pressed && styles.buttonPressed]} onPress={() => setStep("receipt")}>
            <Text style={styles.secondaryButtonText}>{copy.back}</Text>
          </Pressable>

          <Pressable
            style={({ pressed }) => [styles.primaryButton, styles.primaryButtonWide, pressed && styles.buttonPressed]}
            onPress={continueToPayment}
          >
            <Text style={styles.primaryButtonText}>{copy.next}</Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderPaymentStep = () => {
    return (
      <View style={styles.flexFill}>
        <View style={styles.headerWrapCompact}>
          <Text style={styles.title}>{copy.checkoutPaymentTitle}</Text>
          <Text style={styles.subtitle}>{copy.checkoutPaymentSubtitle}</Text>
        </View>

        <ScrollView style={styles.reviewScroll} contentContainerStyle={styles.reviewContent}>
          {renderOrderTotals()}
          {renderPaymentSection()}
        </ScrollView>

        <View style={styles.reviewStartOverWrap}>
          <Pressable
            style={({ pressed }) => [styles.secondaryButton, styles.secondaryButtonFull, pressed && styles.buttonPressed]}
            onPress={resetFlow}
          >
            <Text style={styles.secondaryButtonText}>{copy.startOver}</Text>
          </Pressable>
        </View>

        <View style={styles.reviewFooter}>
          <Pressable style={({ pressed }) => [styles.secondaryButton, styles.secondaryButtonWide, pressed && styles.buttonPressed]} onPress={() => setStep("fulfillment")}>
            <Text style={styles.secondaryButtonText}>{copy.back}</Text>
          </Pressable>

          <Pressable
            style={({ pressed }) => [
              styles.primaryButton,
              styles.primaryButtonWide,
              cart.length === 0 && styles.disabledButton,
              pressed && cart.length > 0 && styles.buttonPressed,
            ]}
            disabled={cart.length === 0}
            onPress={startPayment}
          >
            <Text style={styles.primaryButtonText}>{copy.payNow}</Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderPaying = () => {
    return (
      <View style={styles.centerStep}>
        <Animated.View
          style={[
            styles.spinner,
            {
              transform: [
                {
                  rotate: paySpin.interpolate({
                    inputRange: [0, 1],
                    outputRange: ["0deg", "360deg"],
                  }),
                },
              ],
            },
          ]}
        />
        <Text style={styles.processingTitle}>{copy.processingPayment}</Text>
        <Text style={styles.processingSubtitle}>{copy.processingPaymentSubtitle}</Text>
        <Text style={styles.processingMethodText}>
          {copy.paymentMethodLabel}: {paymentMethodLabel}
        </Text>
      </View>
    );
  };

  const renderThankYou = () => {
    return (
      <View style={styles.centerStep}>
        <View pointerEvents="none" style={styles.thankConfettiLayer}>
          {thankYouConfettiPieces.map((piece) => {
            const progress = thankYouConfettiAnim.interpolate({
              inputRange: [0, piece.delay, 1],
              outputRange: [0, 0, 1],
              extrapolate: "clamp",
            });

            return (
              <Animated.View
                key={piece.id}
                style={[
                  styles.thankConfettiPiece,
                  {
                    left: `${piece.leftPercent}%`,
                    width: piece.size,
                    height: Math.max(8, piece.size * 1.35),
                    backgroundColor: piece.color,
                    opacity: progress.interpolate({
                      inputRange: [0, 0.08, 0.9, 1],
                      outputRange: [0, 1, 1, 0],
                    }),
                    transform: [
                      {
                        translateY: progress.interpolate({
                          inputRange: [0, 1],
                          outputRange: [80, -piece.fallDistance],
                        }),
                      },
                      {
                        translateX: progress.interpolate({
                          inputRange: [0, 0.5, 1],
                          outputRange: [0, piece.sway, 0],
                        }),
                      },
                      {
                        rotate: progress.interpolate({
                          inputRange: [0, 1],
                          outputRange: [`${piece.rotateStart}deg`, `${piece.rotateEnd}deg`],
                        }),
                      },
                    ],
                  },
                ]}
              />
            );
          })}
        </View>

        <View style={styles.thankContent}>
          <View style={styles.thankBadge}>
            <Text style={styles.thankBadgeText}>{copy.orderPlaced}</Text>
          </View>
          <Text style={styles.thankTitle}>{copy.thankYou}</Text>
          {receiptMethod === RECEIPT_METHOD_EMAIL ? (
            <>
              <Text style={styles.thankSubtitle}>{copy.receiptSentTo}</Text>
              <Text style={styles.thankEmail}>{email.trim()}</Text>
            </>
          ) : null}
          {receiptMethod === RECEIPT_METHOD_PHONE ? (
            <>
              <Text style={styles.thankSubtitle}>{copy.receiptSentToPhone}</Text>
              <Text style={styles.thankEmail}>{phone.trim()}</Text>
            </>
          ) : null}
          {receiptMethod === RECEIPT_METHOD_SCREEN ? (
            <Text style={styles.thankSubtitle}>{copy.receiptAvailableOnScreen}</Text>
          ) : null}

          <View style={styles.orderNoBox}>
            <Text style={styles.orderNoLabel}>{copy.orderNumber}</Text>
            <Text style={styles.orderNoValue}>{orderNumber}</Text>
          </View>

          {receiptMethod === RECEIPT_METHOD_SCREEN ? (
            <View style={styles.receiptQrBox}>
              <Text style={styles.receiptQrTitle}>{copy.receiptQrTitle}</Text>
              <QRCode value={receiptQrValue} size={160} />
              <Text style={styles.receiptQrSubtitle}>{copy.receiptQrSubtitle}</Text>
            </View>
          ) : null}

          <Pressable style={({ pressed }) => [styles.primaryButton, styles.primaryButtonFixed, pressed && styles.buttonPressed]} onPress={resetFlow}>
            <Text style={styles.primaryButtonText}>{copy.newOrder}</Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderKioskSettings = () => {
    return (
      <View style={styles.centerStep}>
        <View style={styles.settingsCard}>
          <Text style={styles.kicker}>{copy.kioskSettings}</Text>
          <Text style={styles.settingsTitle}>{copy.deviceControls}</Text>
          <Text style={styles.settingsBody}>
            {copy.settingsBody}
          </Text>
          {kioskDefaultPosId ? (
            <Text style={styles.settingsMeta}>
              POS {kioskDefaultPosId}
            </Text>
          ) : null}

          <Pressable
            style={({ pressed }) => [
              styles.dangerButton,
              styles.primaryButtonFull,
              pressed && styles.buttonPressed,
            ]}
            onPress={openUnconfigureConfirm}
          >
            <Text style={styles.dangerButtonText}>{copy.unconfigureKiosk}</Text>
          </Pressable>

          <Pressable
            style={({ pressed }) => [styles.secondaryButton, styles.secondaryButtonWide, pressed && styles.buttonPressed]}
            onPress={() => setStep("welcome")}
          >
            <Text style={styles.secondaryButtonText}>{copy.back}</Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderSecretGestureHotspots = () => {
    if (!kioskConfig || step === "kioskSettings" || step === "paying") {
      return null;
    }

    return (
      <View pointerEvents="box-none" style={styles.secretHotspotLayer}>
        <Pressable style={[styles.secretHotspot, styles.secretHotspotTopLeft]} onPress={() => registerSecretGesture("top-left")} />
        <Pressable style={[styles.secretHotspot, styles.secretHotspotTopRight]} onPress={() => registerSecretGesture("top-right")} />
        <Pressable style={[styles.secretHotspot, styles.secretHotspotBottomRight]} onPress={() => registerSecretGesture("bottom-right")} />
        <Pressable style={[styles.secretHotspot, styles.secretHotspotBottomLeft]} onPress={() => registerSecretGesture("bottom-left")} />
        <Pressable
          style={[styles.secretHotspot, styles.secretHotspotTopCenter]}
          delayLongPress={900}
          onLongPress={() => registerSecretGesture("top-center-hold")}
        />
      </View>
    );
  };

  const blobLeft = blobWave.interpolate({
    inputRange: [0, 1],
    outputRange: [-24, 22],
  });
  const blobRight = blobWave.interpolate({
    inputRange: [0, 1],
    outputRange: [12, -18],
  });

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.container}>
        <Animated.View
          style={[
            styles.bgBlob,
            styles.bgBlobTop,
            {
              transform: [{ translateX: blobLeft }, { translateY: blobRight }],
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bgBlob,
            styles.bgBlobBottom,
            {
              transform: [{ translateX: blobRight }, { translateY: blobLeft }],
            },
          ]}
        />

        <Animated.View
          style={[
            styles.stepWrap,
            {
              opacity: transitionValue,
              transform: [
                {
                  translateY: transitionValue.interpolate({
                    inputRange: [0, 1],
                    outputRange: [14, 0],
                  }),
                },
              ],
            },
          ]}
        >
          {!kioskConfig && renderDeviceLogin()}
          {kioskConfig && step === "welcome" && renderWelcome()}
          {kioskConfig && step === "browse" && renderBrowse()}
          {kioskConfig && step === "review" && renderReview()}
          {kioskConfig && step === "receipt" && renderReceiptStep()}
          {kioskConfig && step === "fulfillment" && renderFulfillmentStep()}
          {kioskConfig && step === "payment" && renderPaymentStep()}
          {kioskConfig && step === "paying" && renderPaying()}
          {kioskConfig && step === "thankyou" && renderThankYou()}
          {kioskConfig && step === "kioskSettings" && renderKioskSettings()}
        </Animated.View>
        {renderSecretGestureHotspots()}
      </View>

      <Modal
        visible={unconfigureConfirmVisible}
        transparent
        animationType="fade"
        onRequestClose={closeUnconfigureConfirm}
      >
        <View style={styles.settingsConfirmOverlay}>
          <View style={styles.settingsConfirmCard}>
            <Text style={styles.settingsConfirmTitle}>{copy.unconfigureKioskTitle}</Text>
            <Text style={styles.settingsConfirmBody}>
              {copy.unconfigureKioskBody}
            </Text>
            <View style={styles.settingsConfirmActions}>
              <Pressable
                style={({ pressed }) => [
                  styles.secondaryButton,
                  styles.settingsConfirmButton,
                  pressed && styles.buttonPressed,
                ]}
                onPress={closeUnconfigureConfirm}
              >
                <Text style={styles.secondaryButtonText}>{copy.cancel}</Text>
              </Pressable>
              <Pressable
                style={({ pressed }) => [
                  styles.dangerButton,
                  styles.settingsConfirmButton,
                  styles.settingsConfirmDangerButton,
                  pressed && styles.buttonPressed,
                ]}
                onPress={confirmUnconfigureKiosk}
              >
                <Text style={styles.dangerButtonText}>{copy.unconfigure}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={customizerVisible}
        transparent
        animationType="fade"
        onRequestClose={closeCustomizer}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle} numberOfLines={2}>
                {customizerProduct ? translate(customizerProduct.title) : ""}
              </Text>
              <Pressable style={({ pressed }) => [styles.closeButton, pressed && styles.buttonPressed]} onPress={closeCustomizer}>
                <Text style={styles.closeButtonText}>X</Text>
              </Pressable>
            </View>

            {customizerProduct && (
              <ScrollView style={styles.modalScroll} contentContainerStyle={styles.modalScrollContent}>
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionTitle}>{copy.selectedVariant}</Text>
                  <View style={styles.modalVariantSummary}>
                    <Text style={styles.modalVariantSummaryText}>
                      {translate(getVariantName(customizerProduct, customizerVariantIndex, copy.defaultVariant))}
                    </Text>
                    <Text style={styles.modalVariantSummaryPrice}>
                      {formatPrice(getVariantBasePrice(customizerProduct, customizerVariantIndex))}
                    </Text>
                  </View>

                  {customizerAllergens.length > 0 ? (
                    <View style={styles.modalAllergens}>
                      <AllergenBadges allergens={customizerAllergens} size={12} />
                      <Text style={styles.modalAllergensText} numberOfLines={2}>
                        {customizerAllergens.map((allergen) => translate(allergen)).join(" · ")}
                      </Text>
                    </View>
                  ) : null}
                </View>

                {safeArray(customizerProduct.options).map((group) => {
                  const groupChoices = safeArray(customizerOptions[group.id]);
                  const multi = isMultiOption(group);

                  return (
                    <View key={group.id} style={styles.modalSection}>
                      <View style={styles.modalOptionHeader}>
                        <Text style={styles.modalSectionTitle}>{translate(group.title)}</Text>
                        <Text style={styles.modalOptionType}>
                          {group.required ? copy.required : copy.optional} · {multi ? copy.multiple : copy.single}
                        </Text>
                      </View>

                      {safeArray(group.options).map((choice) => {
                        const selected = groupChoices.includes(choice.id);
                        const choicePrice = getChoicePrice(choice, customizerVariantIndex);

                        return (
                          <Pressable
                            key={choice.id}
                            style={({ pressed }) => [
                              styles.choiceRow,
                              selected && styles.choiceRowSelected,
                              pressed && styles.buttonPressed,
                            ]}
                            onPress={() => toggleCustomizerChoice(group, choice.id)}
                          >
                            <View style={[styles.choiceMarker, selected && styles.choiceMarkerSelected]}>
                              {selected ? <Text style={styles.choiceMarkerText}>✓</Text> : null}
                            </View>

                            <Text style={[styles.choiceTitle, selected && styles.choiceTitleSelected]}>
                              {translate(choice.title)}
                            </Text>

                            {choicePrice > 0 ? (
                              <Text style={[styles.choicePrice, selected && styles.choicePriceSelected]}>
                                +{formatPrice(choicePrice)}
                              </Text>
                            ) : (
                              <Text style={styles.choicePrice}>{copy.included}</Text>
                            )}
                          </Pressable>
                        );
                      })}
                    </View>
                  );
                })}

                {customizerError ? <Text style={styles.customizerError}>{customizerError}</Text> : null}
              </ScrollView>
            )}

            <View style={styles.modalFooter}>
              <Text style={styles.modalFooterTotal}>{copy.unitPrice}: {formatPrice(customizerPrice)}</Text>
              <Pressable style={({ pressed }) => [styles.primaryButton, styles.primaryButtonFixed, pressed && styles.buttonPressed]} onPress={confirmCustomizer}>
                <Text style={styles.primaryButtonText}>{copy.addToCart}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

function createStyles(theme, welcomeAdHeight = WELCOME_AD_MIN_HEIGHT) {
  const TEXT_SCALE_SMALL = 1.55;
  const TEXT_SCALE_MEDIUM = 1.35;
  const TEXT_SCALE_LARGE = 1.2;
  const pickTextScale = (value) => {
    if (value <= 14) {
      return TEXT_SCALE_SMALL;
    }
    if (value <= 28) {
      return TEXT_SCALE_MEDIUM;
    }
    return TEXT_SCALE_LARGE;
  };
  const s = (value) => Math.round(value * pickTextScale(value));
  const lh = (value) => Math.round(value * pickTextScale(value));

  return StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: theme.bg,
    },
    container: {
      flex: 1,
      backgroundColor: theme.bg,
    },
    secretHotspotLayer: {
      ...StyleSheet.absoluteFillObject,
      zIndex: 20,
    },
    secretHotspot: {
      position: "absolute",
      width: 34,
      height: 34,
    },
    secretHotspotTopLeft: {
      top: 6,
      left: 6,
    },
    secretHotspotTopRight: {
      top: 6,
      right: 6,
    },
    secretHotspotBottomRight: {
      bottom: 6,
      right: 6,
    },
    secretHotspotBottomLeft: {
      bottom: 6,
      left: 6,
    },
    secretHotspotTopCenter: {
      top: 6,
      left: "50%",
      marginLeft: -17,
    },
    bgBlob: {
      position: "absolute",
      borderRadius: 999,
      opacity: theme.name === "white" ? 0.22 : 0.3,
    },
    bgBlobTop: {
      width: 360,
      height: 360,
      top: -110,
      left: -120,
      backgroundColor: theme.accent,
    },
    bgBlobBottom: {
      width: 300,
      height: 300,
      right: -110,
      bottom: 120,
      backgroundColor: theme.accentSecondary,
    },
    stepWrap: {
      flex: 1,
      paddingHorizontal: 14,
      paddingTop: 8,
    },
    flexFill: {
      flex: 1,
    },
    headerWrap: {
      paddingHorizontal: 4,
      paddingBottom: 10,
    },
    headerWrapCompact: {
      paddingHorizontal: 4,
      paddingBottom: 8,
    },
    kicker: {
      color: theme.accent,
      textTransform: "uppercase",
      fontFamily: "space-mono",
      letterSpacing: 1.1,
      fontSize: s(12),
      marginBottom: 4,
    },
    kioskLogoCompact: {
      width: 86,
      height: 32,
      marginBottom: 6,
    },
    title: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(36),
      lineHeight: lh(40),
      marginBottom: 4,
    },
    subtitle: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(13),
      lineHeight: lh(18),
    },
    themeScroll: {
      maxHeight: 80,
      marginBottom: 12,
    },
    themeScrollCompact: {
      maxHeight: 68,
      marginTop: 8,
    },
    themeScrollContent: {
      alignItems: "center",
      paddingVertical: 2,
      paddingHorizontal: 2,
    },
    themeScrollContentCompact: {
      alignItems: "center",
      paddingVertical: 2,
      paddingHorizontal: 2,
    },
    themeChip: {
      minWidth: 130,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panelSoft,
      paddingVertical: 8,
      paddingHorizontal: 10,
      marginRight: 8,
      alignItems: "center",
      justifyContent: "center",
    },
    themeChipCompact: {
      minWidth: 112,
      borderRadius: 11,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panelSoft,
      paddingVertical: 7,
      paddingHorizontal: 8,
      marginRight: 8,
      alignItems: "center",
      justifyContent: "center",
    },
    themeSwatchRow: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: 4,
    },
    themeSwatch: {
      width: 14,
      height: 14,
      borderRadius: 7,
      marginHorizontal: 2,
      borderWidth: 1,
      borderColor: theme.line,
    },
    themeChipText: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(11),
      textAlign: "center",
    },
    themeChipTextCompact: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(10),
      textAlign: "center",
    },
    categoryScroll: {
      minHeight: 74,
      marginBottom: 8,
    },
    categoryScrollContent: {
      alignItems: "center",
      paddingHorizontal: 2,
      paddingTop: 8,
      paddingBottom: 10,
    },
    categoryChip: {
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      marginRight: 8,
    },
    categoryChipActive: {
      backgroundColor: theme.accent,
      borderColor: theme.accent,
    },
    categoryChipText: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(13),
      lineHeight: lh(16),
    },
    categoryChipTextActive: {
      color: "#ffffff",
    },
    productGridContent: {
      paddingBottom: 130,
    },
    productGridRow: {
      justifyContent: "space-between",
    },
    productGridCell: {
      marginBottom: 12,
    },
    productGridCellHalf: {
      width: "49%",
    },
    productGridCellFull: {
      width: "100%",
    },
    productCard: {
      borderRadius: 20,
      backgroundColor: theme.panel,
      borderWidth: 1,
      borderColor: theme.line,
      overflow: "hidden",
      shadowColor: theme.shadow,
      shadowOpacity: theme.name === "white" ? 0.2 : 0.32,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 8 },
      elevation: 6,
    },
    imageFrame: {
      position: "relative",
      width: "100%",
      height: 220,
      backgroundColor: theme.panelSoft,
    },
    productImage: {
      width: "100%",
      height: "100%",
    },
    imagePlaceholder: {
      justifyContent: "center",
      alignItems: "center",
    },
    imagePlaceholderText: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      textTransform: "uppercase",
      letterSpacing: 1,
    },
    inCartBadge: {
      position: "absolute",
      top: 10,
      right: 10,
      minWidth: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: theme.accent,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 7,
    },
    inCartBadgeText: {
      color: "#ffffff",
      fontFamily: "space-mono",
      fontSize: s(13),
    },
    productMeta: {
      paddingHorizontal: 12,
      paddingVertical: 12,
    },
    productTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(21),
      marginBottom: 3,
    },
    productSubtitle: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      lineHeight: lh(16),
      marginBottom: 8,
    },
    productInfoRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 8,
    },
    productPrice: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(20),
    },
    productInfoHint: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(11),
      textTransform: "uppercase",
      letterSpacing: 0.9,
    },
    allergenWrap: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: 10,
    },
    allergenDots: {
      marginRight: 8,
      maxWidth: 36,
    },
    allergenText: {
      flex: 1,
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(11),
      lineHeight: lh(14),
    },
    productActions: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      columnGap: 8,
    },
    productActionButtonHalf: {
      flex: 1,
      minHeight: 44,
    },
    productActionSymbol: {
      fontFamily: "space-mono",
      fontSize: s(24),
      lineHeight: lh(26),
      textAlign: "center",
    },
    productActionSymbolSecondary: {
      color: theme.text,
    },
    productActionSymbolPrimary: {
      color: "#ffffff",
    },
    primaryButton: {
      minHeight: 46,
      borderRadius: 12,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.accent,
      paddingHorizontal: 16,
    },
    primaryButtonFull: {
      width: "100%",
    },
    primaryButtonFixed: {
      minWidth: 152,
    },
    primaryButtonWide: {
      flex: 1,
      marginLeft: 8,
    },
    primaryButtonText: {
      color: "#ffffff",
      fontFamily: "space-mono",
      fontSize: s(15),
    },
    secondaryButton: {
      minHeight: 46,
      borderRadius: 12,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.panelSoft,
      borderWidth: 1,
      borderColor: theme.line,
      paddingHorizontal: 14,
    },
    secondaryButtonWide: {
      flex: 1,
      marginRight: 8,
    },
    secondaryButtonFull: {
      width: "100%",
    },
    secondaryButtonText: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(14),
    },
    dangerButton: {
      minHeight: 46,
      borderRadius: 12,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.danger,
      paddingHorizontal: 16,
      marginBottom: 10,
    },
    dangerButtonText: {
      color: "#ffffff",
      fontFamily: "space-mono",
      fontSize: s(14),
    },
    settingsConfirmOverlay: {
      flex: 1,
      backgroundColor: "rgba(8, 14, 22, 0.45)",
      justifyContent: "center",
      paddingHorizontal: 18,
    },
    settingsConfirmCard: {
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      paddingHorizontal: 14,
      paddingVertical: 14,
    },
    settingsConfirmTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(20),
      marginBottom: 6,
      textAlign: "center",
    },
    settingsConfirmBody: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(13),
      lineHeight: lh(18),
      marginBottom: 12,
      textAlign: "center",
    },
    settingsConfirmActions: {
      flexDirection: "row",
      alignItems: "center",
    },
    settingsConfirmButton: {
      flex: 1,
    },
    settingsConfirmDangerButton: {
      marginLeft: 8,
      marginBottom: 0,
    },
    disabledButton: {
      opacity: 0.46,
    },
    buttonPressed: {
      opacity: 0.86,
      transform: [{ scale: 0.98 }],
    },
    cartBar: {
      position: "absolute",
      left: 2,
      right: 2,
      bottom: 8,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      paddingHorizontal: 12,
      paddingVertical: 10,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      shadowColor: theme.shadow,
      shadowOpacity: theme.name === "white" ? 0.25 : 0.36,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 8 },
      elevation: 8,
    },
    cartMeta: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(11),
      textTransform: "uppercase",
      letterSpacing: 0.9,
    },
    cartTotal: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(25),
    },
    loadingState: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 20,
    },
    loadingText: {
      marginTop: 12,
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(14),
      textAlign: "center",
    },
    errorTitle: {
      color: theme.danger,
      fontFamily: "space-mono",
      fontSize: s(20),
      marginBottom: 8,
      textAlign: "center",
    },
    errorBody: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(13),
      lineHeight: lh(19),
      marginBottom: 12,
      textAlign: "center",
    },
    reviewScroll: {
      flex: 1,
    },
    reviewContent: {
      paddingBottom: 20,
    },
    emptyText: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(14),
      marginTop: 12,
    },
    reviewItem: {
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      paddingHorizontal: 12,
      paddingVertical: 10,
      marginBottom: 10,
    },
    reviewMain: {
      marginBottom: 8,
    },
    reviewTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(17),
      marginBottom: 2,
    },
    reviewVariant: {
      color: theme.accent,
      fontFamily: "space-mono",
      fontSize: s(12),
      marginBottom: 4,
    },
    reviewOptions: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      lineHeight: lh(17),
      marginBottom: 5,
    },
    reviewAllergenWrap: {
      flexDirection: "row",
      alignItems: "center",
    },
    reviewAllergenText: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(11),
      marginLeft: 8,
      flex: 1,
    },
    itemNoteBlock: {
      marginTop: 8,
    },
    itemNoteLabel: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(11),
      marginBottom: 5,
      textTransform: "uppercase",
      letterSpacing: 0.6,
    },
    itemNoteInput: {
      minHeight: 44,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panelSoft,
      paddingHorizontal: 10,
      paddingVertical: 8,
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(12),
      textAlignVertical: "top",
    },
    reviewControls: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 8,
    },
    qtyControl: {
      width: 36,
      height: 36,
      borderRadius: 9,
      borderWidth: 1,
      borderColor: theme.line,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.panelSoft,
    },
    qtyControlText: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(24),
      lineHeight: lh(26),
      marginTop: -1,
    },
    qtyText: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(16),
      marginHorizontal: 14,
      minWidth: 20,
      textAlign: "center",
    },
    reviewLineTotal: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(18),
      textAlign: "right",
    },
    receiptBox: {
      borderRadius: 14,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      padding: 12,
      marginTop: 2,
      marginBottom: 12,
    },
    receiptRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 6,
    },
    receiptLabel: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(14),
    },
    receiptValue: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(14),
    },
    receiptDivider: {
      height: 1,
      backgroundColor: theme.line,
      marginVertical: 6,
    },
    receiptTotalLabel: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(16),
    },
    receiptTotalValue: {
      color: theme.accent,
      fontFamily: "space-mono",
      fontSize: s(18),
    },
    checkoutSection: {
      borderRadius: 14,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      padding: 12,
      marginBottom: 12,
    },
    checkoutSectionTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(16),
      marginBottom: 4,
    },
    checkoutSectionSubtitle: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      lineHeight: lh(17),
      marginBottom: 8,
    },
    checkoutOptionRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
      marginBottom: 6,
    },
    checkoutOptionChip: {
      width: "32%",
      minHeight: 116,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panelSoft,
      paddingHorizontal: 8,
      paddingVertical: 12,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 8,
    },
    checkoutOptionChipWide: {
      width: "48.8%",
      minHeight: 122,
    },
    checkoutOptionChipActive: {
      borderColor: theme.accent,
      backgroundColor: theme.accentSoft,
    },
    checkoutOptionIcon: {
      marginBottom: 6,
    },
    checkoutOptionText: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      lineHeight: lh(15),
      textAlign: "center",
    },
    checkoutOptionTextActive: {
      color: theme.text,
    },
    checkoutHint: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      lineHeight: lh(18),
      marginBottom: 6,
    },
    emailBlock: {
      marginBottom: 18,
    },
    orderNoteBlock: {
      marginBottom: 14,
    },
    emailLabel: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(14),
      marginBottom: 8,
    },
    emailInput: {
      minHeight: 50,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      paddingHorizontal: 12,
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(15),
    },
    checkoutMultilineInput: {
      minHeight: 86,
      textAlignVertical: "top",
      paddingTop: 10,
      paddingBottom: 10,
    },
    emailInputError: {
      borderColor: theme.danger,
    },
    emailError: {
      marginTop: 7,
      color: theme.danger,
      fontFamily: "space-mono",
      fontSize: s(12),
    },
    deviceLoginPrimaryButton: {
      marginTop: 12,
    },
    reviewFooter: {
      flexDirection: "row",
      paddingBottom: 8,
    },
    reviewStartOverWrap: {
      marginBottom: 8,
    },
    centerStep: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 20,
    },
    welcomeLayout: {
      flex: 1,
      width: "100%",
      justifyContent: "center",
      paddingBottom: welcomeAdHeight + 14,
    },
    welcomeLayoutNoAd: {
      paddingBottom: 0,
    },
    welcomeHero: {
      position: "absolute",
      top: 10,
      left: 0,
      right: 0,
      alignItems: "center",
    },
    welcomeCard: {
      width: "100%",
      borderRadius: 22,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      paddingHorizontal: 16,
      paddingVertical: 18,
      shadowColor: theme.shadow,
      shadowOpacity: theme.name === "white" ? 0.22 : 0.3,
      shadowRadius: 16,
      shadowOffset: { width: 0, height: 10 },
      elevation: 7,
    },
    welcomeCardWithLogoHero: {
      marginTop: 428,
    },
    welcomeCardNoLogoHero: {
      marginTop: 262,
    },
    welcomeAdWrap: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 22,
      height: welcomeAdHeight,
    },
    welcomeAdFrame: {
      flex: 1,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.line,
      overflow: "hidden",
      backgroundColor: theme.panel,
      shadowColor: theme.shadow,
      shadowOpacity: theme.name === "white" ? 0.18 : 0.28,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 6 },
      elevation: 5,
    },
    welcomeAdWebView: {
      flex: 1,
      backgroundColor: "transparent",
    },
    settingsCard: {
      width: "100%",
      borderRadius: 22,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      paddingHorizontal: 16,
      paddingVertical: 18,
      shadowColor: theme.shadow,
      shadowOpacity: theme.name === "white" ? 0.22 : 0.3,
      shadowRadius: 16,
      shadowOffset: { width: 0, height: 10 },
      elevation: 7,
    },
    settingsTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(30),
      lineHeight: lh(34),
      marginBottom: 8,
    },
    settingsBody: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(14),
      lineHeight: lh(20),
      marginBottom: 12,
    },
    settingsMeta: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      marginBottom: 14,
    },
    kioskLogo: {
      width: 360,
      height: 172,
      alignSelf: "center",
      marginBottom: 14,
    },
    welcomeHeroTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(58),
      lineHeight: lh(62),
      marginBottom: 10,
      textAlign: "center",
    },
    orderHereWrap: {
      position: "absolute",
      left: 0,
      right: 0,
      alignItems: "center",
      zIndex: 4,
    },
    orderHereWrapWithLogo: {
      top: 267,
    },
    orderHereWrapNoLogo: {
      top: 92,
    },
    orderHereText: {
      color: theme.accentSecondary,
      fontFamily: "space-mono",
      fontSize: s(62),
      lineHeight: lh(66),
      letterSpacing: 2,
      textAlign: "center",
    },
    orderHereArrow: {
      color: theme.accentSecondary,
      fontFamily: "space-mono",
      fontSize: s(92),
      lineHeight: lh(96),
      marginTop: -2,
      textAlign: "center",
    },
    welcomeTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(52),
      lineHeight: lh(56),
      marginBottom: 10,
      textAlign: "center",
    },
    welcomeSubtitle: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(14),
      lineHeight: lh(20),
      textAlign: "center",
      marginBottom: 14,
    },
    deviceMetaText: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      textAlign: "center",
      marginBottom: 12,
    },
    deviceHint: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(11),
      marginTop: 8,
      marginBottom: 12,
      textAlign: "center",
    },
    languageTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(14),
      marginBottom: 8,
      textAlign: "center",
    },
    themeTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(14),
      marginBottom: 8,
      textAlign: "center",
    },
    languageGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "center",
      marginBottom: 14,
    },
    languageChip: {
      minWidth: 120,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panelSoft,
      paddingVertical: 8,
      paddingHorizontal: 10,
      margin: 5,
      alignItems: "center",
      justifyContent: "center",
    },
    languageChipActive: {
      borderColor: theme.accent,
      backgroundColor: theme.accentSoft,
    },
    languageFlag: {
      fontSize: s(20),
      marginBottom: 2,
    },
    languageChipText: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
    },
    languageChipTextActive: {
      color: theme.text,
    },
    spinner: {
      width: 78,
      height: 78,
      borderRadius: 39,
      borderWidth: 6,
      borderColor: theme.line,
      borderTopColor: theme.accent,
      marginBottom: 14,
    },
    processingTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(26),
      marginBottom: 6,
      textAlign: "center",
    },
    processingSubtitle: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(14),
      textAlign: "center",
    },
    processingMethodText: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(14),
      marginTop: 8,
      textAlign: "center",
    },
    thankBadge: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      marginBottom: 12,
    },
    thankBadgeText: {
      color: theme.warning,
      fontFamily: "space-mono",
      fontSize: s(12),
      textTransform: "uppercase",
      letterSpacing: 0.8,
    },
    thankConfettiLayer: {
      ...StyleSheet.absoluteFillObject,
      overflow: "hidden",
      zIndex: 0,
    },
    thankConfettiPiece: {
      position: "absolute",
      bottom: -60,
      borderRadius: 3,
    },
    thankContent: {
      zIndex: 1,
      alignItems: "center",
      width: "100%",
    },
    thankTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(48),
      lineHeight: lh(52),
      marginBottom: 6,
      textAlign: "center",
    },
    thankSubtitle: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(14),
      textAlign: "center",
    },
    thankEmail: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(18),
      marginTop: 6,
      marginBottom: 16,
      textAlign: "center",
    },
    orderNoBox: {
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      paddingHorizontal: 22,
      paddingVertical: 14,
      alignItems: "center",
      marginBottom: 18,
    },
    orderNoLabel: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      textTransform: "uppercase",
      letterSpacing: 0.9,
      marginBottom: 3,
    },
    orderNoValue: {
      color: theme.accent,
      fontFamily: "space-mono",
      fontSize: s(30),
      letterSpacing: 0.8,
    },
    receiptQrBox: {
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      paddingVertical: 12,
      paddingHorizontal: 14,
      alignItems: "center",
      marginBottom: 16,
    },
    receiptQrTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(16),
      marginBottom: 8,
      textAlign: "center",
    },
    receiptQrSubtitle: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      marginTop: 8,
      textAlign: "center",
      maxWidth: 320,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(10, 20, 35, 0.35)",
      justifyContent: "center",
      padding: 16,
    },
    modalCard: {
      maxHeight: "90%",
      backgroundColor: theme.panel,
      borderRadius: 18,
      borderWidth: 1,
      borderColor: theme.line,
      overflow: "hidden",
    },
    modalHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottomWidth: 1,
      borderBottomColor: theme.line,
      paddingHorizontal: 12,
      paddingVertical: 10,
    },
    modalTitle: {
      flex: 1,
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(18),
      marginRight: 8,
    },
    closeButton: {
      width: 34,
      height: 34,
      borderRadius: 17,
      backgroundColor: theme.panelSoft,
      borderWidth: 1,
      borderColor: theme.line,
      alignItems: "center",
      justifyContent: "center",
    },
    closeButtonText: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(14),
    },
    modalScroll: {
      maxHeight: 460,
    },
    modalScrollContent: {
      paddingHorizontal: 12,
      paddingBottom: 8,
    },
    modalSection: {
      paddingTop: 12,
      paddingBottom: 6,
      borderBottomWidth: 1,
      borderBottomColor: theme.line,
    },
    modalSectionTitle: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(14),
      marginBottom: 8,
    },
    modalVariantSummary: {
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panelSoft,
      paddingHorizontal: 10,
      paddingVertical: 8,
      marginBottom: 8,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    modalVariantSummaryText: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(13),
      flexShrink: 1,
      marginRight: 8,
    },
    modalVariantSummaryPrice: {
      color: theme.accent,
      fontFamily: "space-mono",
      fontSize: s(13),
    },
    variantRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      marginBottom: 6,
    },
    variantChip: {
      minWidth: 120,
      paddingHorizontal: 10,
      paddingVertical: 8,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panelSoft,
      marginRight: 8,
      marginBottom: 8,
    },
    variantChipActive: {
      borderColor: theme.accent,
      backgroundColor: theme.accentSoft,
    },
    variantChipText: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(13),
      marginBottom: 2,
    },
    variantChipTextActive: {
      color: theme.text,
    },
    variantChipPrice: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
    },
    variantChipPriceActive: {
      color: theme.accent,
    },
    modalAllergens: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: 4,
    },
    modalAllergensText: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(11),
      marginLeft: 8,
      flex: 1,
    },
    modalOptionHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 6,
    },
    modalOptionType: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(11),
    },
    choiceRow: {
      flexDirection: "row",
      alignItems: "center",
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panelSoft,
      paddingHorizontal: 10,
      paddingVertical: 9,
      marginBottom: 7,
    },
    choiceRowSelected: {
      borderColor: theme.accent,
      backgroundColor: theme.accentSoft,
    },
    choiceMarker: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.line,
      backgroundColor: theme.panel,
      alignItems: "center",
      justifyContent: "center",
      marginRight: 9,
    },
    choiceMarkerSelected: {
      borderColor: theme.accent,
      backgroundColor: theme.accent,
    },
    choiceMarkerText: {
      color: "#ffffff",
      fontSize: s(12),
      lineHeight: lh(12),
      fontFamily: "space-mono",
    },
    choiceTitle: {
      flex: 1,
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(13),
      marginRight: 10,
    },
    choiceTitleSelected: {
      color: theme.text,
    },
    choicePrice: {
      color: theme.textMuted,
      fontFamily: "space-mono",
      fontSize: s(12),
      textAlign: "right",
    },
    choicePriceSelected: {
      color: theme.accent,
    },
    customizerError: {
      color: theme.danger,
      fontFamily: "space-mono",
      fontSize: s(12),
      marginTop: 10,
    },
    modalFooter: {
      borderTopWidth: 1,
      borderTopColor: theme.line,
      paddingHorizontal: 12,
      paddingVertical: 10,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    modalFooterTotal: {
      color: theme.text,
      fontFamily: "space-mono",
      fontSize: s(14),
      marginRight: 8,
      flexShrink: 1,
    },
  });
}
